export type Language = "pt" | "en" | "es";

export const translations: Record<Language, Record<string, string>> = {
  pt: {
    // Common
    "app.name": "Orzio",
    "common.save": "Guardar",
    "common.cancel": "Cancelar",
    "common.next": "Seguinte",
    "common.previous": "Anterior",
    "common.delete": "Eliminar",
    "common.edit": "Editar",
    "common.duplicate": "Duplicar",
    "common.export": "Exportar",
    "common.add": "Adicionar",
    "common.remove": "Remover",
    "common.loading": "A carregar...",
    "common.close": "Fechar",
    "common.retry": "Tentar novamente",
    "common.error": "Erro",
    "common.success": "Sucesso",
    "common.total": "Total",
    "common.subtotal": "Subtotal",
    "common.unit": "Unidade",
    "common.quantity": "Quantidade",
    "common.item": "Item",
    "common.price": "Preço",
    "common.description": "Descrição",
    "common.observations": "Observações",
    "common.conditions": "Condições",
    "common.step": "Passo",
    "common.of": "de",

    // Error boundary
    "errorBoundary.title": "A aplicação encontrou um erro inesperado.",
    "errorBoundary.description": "Pode tentar continuar, recarregar a página ou voltar ao início. Se o problema persistir, contacte o suporte com a referência abaixo.",
    "errorBoundary.reference": "Referência:",
    "errorBoundary.retry": "Tentar novamente",
    "errorBoundary.reload": "Recarregar página",
    "errorBoundary.goHome": "Voltar ao início",
    "errorBoundary.contactSupport": "Contactar suporte",
    "errorBoundary.devDetails": "Detalhes técnicos (dev)",
    "errorBoundary.mail.subject": "[Orzio] Erro inesperado{eventIdSuffix}",
    "errorBoundary.mail.greeting": "Olá equipa Orzio,",
    "errorBoundary.mail.intro": "Ocorreu um erro inesperado na aplicação.",
    "errorBoundary.mail.reference": "Referência interna: {eventId}",
    "errorBoundary.mail.message": "Mensagem: {message}",
    "errorBoundary.mail.url": "URL: {url}",
    "errorBoundary.mail.userAction": "Descrição do que estava a fazer:",
    
    // Auth
    "auth.login": "Entrar",
    "auth.signup": "Criar Conta",
    "auth.logout": "Sair",
    "auth.email": "Email",
    "auth.password": "Password",
    "auth.emailPlaceholder": "engenheiro@exemplo.pt",
    "auth.productTagline": "Orçamentação técnica para construção",
    "auth.signupTitle": "Criar Conta",
    "auth.loginTitle": "Entrar",
    "auth.signupDesc": "Crie a sua conta para começar a estruturar orçamentos",
    "auth.loginDesc": "Entre na sua conta para continuar",
    "auth.loginWithGoogle": "Entrar com Google",
    "auth.signupWithGoogle": "Criar conta com Google",
    "auth.modeSelector": "Escolher modo de autenticação",
    "auth.modeCurrent": "Modo atual",
    "auth.switchToLogin": "Mudar para entrar",
    "auth.switchToSignup": "Mudar para criar conta",
    "auth.orEmail": "ou email",
    "auth.forgotPassword": "Esqueceu a password?",
    "auth.resetTitle": "Recuperar password",
    "auth.resetDesc": "Indique o email da conta e enviaremos uma ligação de recuperação.",
    "auth.sendReset": "Enviar email de recuperação",
    "auth.resetEmailSent": "Email enviado",
    "auth.resetEmailDesc": "Verifique a sua caixa de entrada para definir uma nova password.",
    "auth.resetError": "Não foi possível processar a recuperação de password.",
    "auth.newPasswordTitle": "Definir nova password",
    "auth.newPasswordDesc": "Escolha uma nova password para continuar a usar o Orzio.",
    "auth.newPassword": "Nova password",
    "auth.confirmPassword": "Confirmar nova password",
    "auth.passwordMismatch": "As passwords não coincidem.",
    "auth.saveNewPassword": "Guardar nova password",
    "auth.passwordUpdated": "Password atualizada",
    "auth.passwordUpdatedDesc": "Pode continuar a usar o Orzio com a nova password.",
    "auth.recoveryChecking": "A validar a ligação de recuperação...",
    "auth.recoveryLinkInvalid": "Esta ligação de recuperação é inválida ou expirou. Peça um novo email para redefinir a password.",
    "auth.backToLogin": "Voltar para entrar",
    "auth.googleError": "Não foi possível iniciar sessão com Google.",
    "auth.hasAccount": "Já tem conta?",
    "auth.noAccount": "Não tem conta?",
    "auth.accountCreated": "Conta criada",
    "auth.checkEmail": "Verifique o seu email para confirmar a conta.",
    "auth.invalidCredentials": "Email ou password incorretos.",
    "auth.emailExists": "Este email já está registado.",
    "auth.invalidEmail": "Email inválido",
    "auth.passwordTooShort": "A password deve ter pelo menos 6 caracteres",
    
    // Landing
    "landing.hero.title": "Orçamentos Técnicos Estruturados",
    "landing.hero.subtitle": "Ferramenta profissional para engenheiros e gabinetes técnicos. Estruture, justifique e padronize os seus orçamentos de engenharia.",
    "landing.cta.start": "Começar Agora",
    "landing.cta.demo": "Ver Demonstração",
    "landing.cta.create": "Criar Conta Gratuita",
    "landing.principles.title": "Princípios Fundamentais",
    "landing.principles.deterministic.title": "Cálculos Determinísticos",
    "landing.principles.deterministic.desc": "Todos os valores são calculados com lógica precisa. Sem estimativas de IA para números críticos.",
    "landing.principles.structured.title": "Fluxo Estruturado",
    "landing.principles.structured.desc": "Processo guiado em etapas obrigatórias. Garanta que nenhum elemento essencial fique de fora.",
    "landing.principles.separation.title": "Separação Clara",
    "landing.principles.separation.desc": "Distinção entre dados técnicos internos e informação visível para o cliente.",
    "landing.features.title": "Funcionalidades",
    "landing.features.guided": "Criação guiada de orçamentos técnicos",
    "landing.features.costs": "Gestão de blocos de custos e mão-de-obra",
    "landing.features.risk": "Cálculo automático de risco e margem",
    "landing.features.assumptions": "Pressupostos técnicos estruturados",
    "landing.features.pdf": "Exportação PDF profissional",
    "landing.features.versions": "Histórico de versões",
    "landing.features.ai": "Sugestões de âmbito assistidas por IA",
    "landing.features.gdpr": "PDF com vista interna e vista cliente separadas",
    "landing.features.projects": "Orçamentos de Desenvolvimento de projetos",
    "landing.features.works": "Orçamentos de Execução de Obras",
    "landing.cta.title": "Profissionalize os seus orçamentos",
    "landing.cta.desc": "Junte-se a engenheiros e gabinetes técnicos que já estruturam os seus orçamentos de forma profissional.",
    "landing.footer": "Contexto Europeu. Todos os direitos reservados.",
    
    // Dashboard
    "dashboard.title": "Orçamentos",
    "dashboard.subtitle": "Gerir e criar orçamentos técnicos",
    "dashboard.newBudget": "Novo Orçamento",
    "dashboard.stats.total": "Total de Orçamentos",
    "dashboard.stats.drafts": "Rascunhos",
    "dashboard.stats.sent": "Enviados",
    "dashboard.stats.approved": "Aprovados",
    "dashboard.stats.rejected": "Rejeitados",
    "dashboard.stats.expired": "Expirados",
    "dashboard.filters": "Filtros",
    "dashboard.allBudgets": "Todos",
    "dashboard.list.title": "Lista de Orçamentos",
    "dashboard.list.version": "Versão",
    "dashboard.list.totalPrice": "Preço Total",
    "dashboard.status.draft": "Rascunho",
    "dashboard.status.sent": "Enviado",
    "dashboard.status.approved": "Aprovado",
    "dashboard.status.rejected": "Rejeitado",
    "dashboard.status.expired": "Expirado",
    "dashboard.approvalValue": "Valor final acordado?",
    "dashboard.discountApplied": "Desconto aplicado",
    "dashboard.reopen": "Reabrir",
    "dashboard.validity": "Validade",
    "dashboard.validityDays": "dias de validade",
    "dashboard.exportPdf": "Exportar PDF",
    "dashboard.markSent": "Marcar como Enviado",
    "dashboard.markApproved": "Marcar como Aprovado",
    "dashboard.markRejected": "Marcar como Rejeitado",
    "dashboard.reopenBudget": "Reabrir Orçamento",
    "dashboard.approveTitle": "Aprovar Orçamento",
    "dashboard.approveDesc": "Introduza o valor final acordado com o cliente.",
    "dashboard.agreedValue": "Valor acordado",
    "dashboard.unplannedDiscount": "Desconto não planeado",
    "dashboard.confirm": "Confirmar",
    "dashboard.statusChanged": "Estado alterado com sucesso",
    "dashboard.expired.auto": "Expirado automaticamente",
    "dashboard.preSendReview.title": "Revisão antes de enviar",
    "dashboard.preSendReview.description": "O Orzio encontrou pontos que convém rever antes de marcar o orçamento como enviado.",
    "dashboard.preSendReview.continue": "Enviar mesmo assim",
    "dashboard.preSendReview.back": "Voltar ao orçamento",
    "dashboard.preSendReview.summary": "Resumo",
    
    // Budget Types
    "budgetType.title": "Tipo de Orçamento",
    "budgetType.subtitle": "Escolha o tipo de orçamento que pretende criar",
    "budgetType.projects.title": "Desenvolvimento de projetos",
    "budgetType.projects.desc": "Orçamentos para serviços de engenharia e projeto técnico. Baseado em horas e especialidades.",
    "budgetType.works.title": "Execução de Obras",
    "budgetType.works.desc": "Orçamentos para construção e empreitadas. Baseado em itens, quantidades e preços unitários.",
    "budgetType.unavailable": "Não incluído no plano",
    "budgetType.warning": "Esta escolha não pode ser alterada após a criação do orçamento.",
    
    // Wizard Steps
    "wizard.step.type": "Tipo",
    "wizard.step.identification": "Identificação",
    "wizard.step.scope": "Âmbito",
    "wizard.step.costs": "Custos",
    "wizard.step.risk": "Risco",
    "wizard.step.assumptions": "Pressupostos",
    "wizard.step.executionTime": "Prazo",
    "wizard.step.review": "Revisão",
    "wizard.newBudget": "Novo Orçamento",
    "wizard.saveBudget": "Guardar Orçamento",
    "wizard.saving": "A guardar...",
    "wizard.saved": "Orçamento guardado",
    "wizard.savedDesc": "O seu orçamento foi guardado com sucesso.",
    
    // Identification Step
    "identification.title": "Identificação do Projeto",
    "identification.subtitle": "Informação básica sobre o projeto a orçamentar",
    "identification.projectName": "Nome do projeto",
    "identification.projectNamePlaceholder": "Ex: Edifício Residencial Tejo",
    "identification.projectType": "Tipo de projeto",
    "identification.projectType.newBuild": "Construção Nova",
    "identification.projectType.rehabilitation": "Reabilitação",
    "identification.projectType.industrial": "Industrial",
    "identification.projectType.technicalService": "Serviço Técnico",
    "identification.projectType.other": "Outro",
    "identification.projectType.otherPlaceholder": "Especifique o tipo de projeto...",
    "identification.selectType": "Selecionar tipo",
    "identification.selectLevel": "Selecionar nível",
    "identification.location": "Localização",
    "identification.locationPlaceholder": "Ex: Lisboa, Portugal",
    "identification.clientType": "Tipo de cliente",
    "identification.clientType.private": "Particular",
    "identification.clientType.company": "Empresa",
    "identification.clientType.public": "Entidade Pública",
    "identification.definitionLevel": "Nível de Definição do Projeto",
    "identification.definitionLevel.low": "Baixo (Ideia/Conceito)",
    "identification.definitionLevel.medium": "Médio (Estudo Prévio)",
    "identification.definitionLevel.high": "Alto (Projeto Base/Execução)",
    "identification.definitionLevelHint": "Quanto menor a definição, maior o risco associado",
    
    // Scope Step (Projects)
    "scope.projects.title": "Âmbito Técnico",
    "scope.projects.subtitle": "Selecione os serviços de engenharia incluídos neste orçamento",
    "scope.projects.suggest": "Sugerir Âmbito",
    "scope.projects.additionalScope": "Âmbito Adicional",
    "scope.projects.additionalScopePlaceholder": "Descreva serviços adicionais não listados acima...",
    "scope.projects.selected": "serviço(s) selecionado(s)",
    "scope.projects.selectAtLeastOne": "Selecione pelo menos um serviço para continuar",
    
    // Scope Step (Works)
    "scope.works.title": "Âmbito da Obra",
    "scope.works.subtitle": "Defina a tipologia e condições gerais da obra",
    "scope.works.typology": "Tipologia de Obra",
    "scope.works.typology.construcao_nova": "Construção Nova",
    "scope.works.typology.reabilitacao": "Reabilitação / Remodelação",
    "scope.works.typology.industrial_l1": "Industrial",
    "scope.works.typology.obra_parcial": "Obra Parcial / Especializada",
    "scope.works.typology.infraestrutura": "Infraestrutura",
    "scope.works.typology.instalacoes_tecnicas": "Instalações Técnicas",
    "scope.works.typology.manutencao": "Manutenção / Conservação",
    "scope.works.typology.outra": "Outra",
    "scope.works.typology.outraPlaceholder": "Especifique a tipologia...",
    "scope.works.subTypologyLabel": "Subtipologia (opcional)",
    "scope.works.sub.habitacao": "Habitação",
    "scope.works.sub.comercial": "Comercial",
    "scope.works.sub.misto": "Misto",
    "scope.works.sub.edificio_publico": "Edifício Público",
    "scope.works.sub.estrutural": "Estrutural",
    "scope.works.sub.interior": "Interior",
    "scope.works.sub.fachadas": "Fachadas",
    "scope.works.sub.integral": "Integral",
    "scope.works.sub.nave": "Nave Industrial",
    "scope.works.sub.plataformas": "Plataformas",
    "scope.works.sub.instalacoes": "Instalações",
    "scope.works.sub.estrutura_metalica": "Estrutura Metálica",
    "scope.works.sub.coberturas": "Coberturas",
    "scope.works.sub.fachadas_parcial": "Fachadas",
    "scope.works.sub.impermeabilizacao": "Impermeabilização",
    "scope.works.sub.arruamentos": "Arruamentos",
    "scope.works.sub.saneamento": "Saneamento",
    "scope.works.sub.abastecimento": "Abastecimento",
    "scope.works.sub.avac": "AVAC",
    "scope.works.sub.eletricidade": "Eletricidade",
    "scope.works.sub.telecomunicacoes": "Telecomunicações",
    "scope.works.sub.gas": "Gás",
    "scope.works.sub.preventiva": "Preventiva",
    "scope.works.sub.corretiva": "Corretiva",
    "scope.works.sub.conservacao": "Conservação",
    "scope.works.sub.other": "Outra",
    "scope.works.sub.otherPlaceholder": "Especifique...",
    "scope.works.selectionsCount": "seleção(ões)",
    "scope.works.generalObservations": "Observações Gerais",
    "scope.works.generalObservationsPlaceholder": "Descreva as condições gerais da obra...",
    "scope.works.exclusions": "Condições e Exclusões",
    "scope.works.exclusionsPlaceholder": "Liste as condições e exclusões...",
    
    // Cost Blocks Step (Projects)
    "costs.projects.title": "Blocos de Custos",
    "costs.projects.subtitle": "Defina os custos de mão-de-obra e outros custos associados",
    "costs.projects.labor": "Mão-de-Obra",
    "costs.projects.indirect": "Custos Indiretos",
    "costs.projects.addLine": "Adicionar Linha",
    "costs.projects.addBlock": "Adicionar Bloco de Custos",
    "costs.projects.role": "Função",
    "costs.projects.hours": "Horas",
    "costs.projects.hourlyRate": "€/Hora",
    "costs.projects.baseCost": "Custo Total Base",
    "costs.projects.beforeRiskMargin": "Antes de risco e margem",
    "costs.projects.roles.seniorEngineer": "Engenheiro Sénior",
    "costs.projects.roles.engineer": "Engenheiro",
    "costs.projects.roles.juniorEngineer": "Engenheiro Júnior",
    "costs.projects.roles.technician": "Técnico",
    "costs.projects.roles.draftsman": "Desenhador",
    "costs.projects.roles.admin": "Administrativo",
    
    // Cost Items Step (Works)
    "costs.works.title": "Mapa de Quantidades",
    "costs.works.subtitle": "Defina os itens de trabalho com quantidades e preços unitários",
    "costs.works.import.title": "Importar quantitativos Orzio Takeoff",
    "costs.works.import.subtitle": "Aceita ficheiros .xlsx emitidos pelo Add-in Orzio Takeoff para Revit com schema técnico válido.",
    "costs.works.import.button": "Importar XLSX",
    "costs.works.import.itemsImported": "itens importados de",
    "costs.works.import.genericError": "Não foi possível importar o ficheiro Orzio Takeoff.",
    "costs.works.addItem": "Adicionar Item",
    "costs.works.itemDescription": "Descrição do Item",
    "costs.works.itemDescriptionPlaceholder": "Ex: Fornecimento e montagem de estrutura metálica...",
    "costs.works.unitPrice": "Preço Unitário",
    "costs.works.totalPrice": "Preço Total",
    "costs.works.composition": "Composição",
    "costs.works.openComposition": "Abrir Composição",
    "costs.works.closeComposition": "Fechar Composição",
    "costs.works.addSubItem": "Adicionar Sub-item",
    "costs.works.subItem.labor": "Mão-de-Obra",
    "costs.works.subItem.materials": "Materiais",
    "costs.works.subItem.equipment": "Equipamento",
    "costs.works.subItem.other": "Outros",
    "costs.works.referencePrice": "Preço de Referência",
    "costs.works.referencePriceHint": "Opcional - usado apenas como orientação",
    "costs.works.units.m2": "m²",
    "costs.works.units.ml": "ml",
    "costs.works.units.kg": "kg",
    "costs.works.units.un": "un",
    "costs.works.units.h": "h",
    "costs.works.units.vg": "vg",
    "costs.works.units.m": "m",
    "costs.works.units.m3": "m³",
    "costs.works.units.t": "t",
    "costs.works.units.day": "dia",
    "costs.works.units.week": "semana",
    "costs.works.units.lot": "lote",
    "costs.works.units.percent": "%",
    "costs.works.units.global": "global",
    "costs.works.totalBase": "Total Base",
    
    // Risk & Margin Step
    "risk.title": "Risco e Margem",
    "risk.subtitle": "Defina o nível de risco e a margem de lucro pretendida",
    "risk.level": "Nível de Risco do Projeto",
    "risk.level.low": "Baixo",
    "risk.level.low.desc": "Projeto bem definido, cliente experiente, condições conhecidas",
    "risk.level.medium": "Médio",
    "risk.level.medium.desc": "Definição parcial, algumas incertezas técnicas ou de âmbito",
    "risk.level.high": "Alto",
    "risk.level.high.desc": "Projeto com muitas incógnitas, cliente inexperiente, prazos apertados",
    "risk.customPercentage": "Percentagem de Risco Personalizada",
    "risk.margin": "Margem de Lucro",
    "risk.marginBelowMin": "Margem abaixo do mínimo definido pela organização",
    "risk.indirectCosts": "Custos Indiretos",
    "risk.indirectCostsHint": "Encargos gerais, administração, etc.",
    "risk.calculation": "Cálculo em Tempo Real",
    "risk.baseCost": "Custo Base",
    "risk.riskProvision": "Provisão Risco",
    "risk.costWithRisk": "Custo com Risco",
    "risk.marginValue": "Margem",
    "risk.finalPrice": "Preço Final",
    "risk.marginBelowMinWithValue": "Margem abaixo do mínimo definido pela organização ({min}%).",
    "risk.ai.title": "Sugestão de margem com IA",
    "risk.ai.loading": "A analisar histórico e contexto comercial...",
    "risk.ai.sampleConfidence": "Amostra: {sampleSize} orçamento(s) · Confiança {confidence}",
    "risk.ai.apply": "Aplicar {margin}%",
    "risk.ai.error": "Não foi possível sugerir a margem.",
    "risk.ai.confidence.high": "Alta",
    "risk.ai.confidence.medium": "Média",
    "risk.ai.confidence.low": "Baixa",

    // Budget editor
    "budgetEditor.unknownError": "Erro desconhecido.",
    "budgetEditor.createError": "Não foi possível criar o orçamento",
    "budgetEditor.createErrorWithMessage": "Não foi possível criar o orçamento: {message}",
    "budgetEditor.snapshotLoadError": "Não foi possível carregar o snapshot. A mostrar dados demo.",
    "budgetEditor.demoMode": "Editor em modo demo",
    "budgetEditor.loadBudgetFailure": "Falha ao carregar o orçamento.",
    "budgetEditor.autoSaveError": "Não foi possível guardar automaticamente",
    "budgetEditor.manualSaveSuccess": "Orçamento guardado",
    "budgetEditor.manualSaveSuccessDescription": "As alterações foram gravadas com sucesso.",
    "budgetEditor.manualSaveError": "Não foi possível guardar",
    "budgetEditor.approvedLocally": "Orçamento aprovado localmente",
    "budgetEditor.saveToOpenWork": "Crie ou guarde o orçamento para abrir a obra.",
    "budgetEditor.approved": "Orçamento aprovado",
    "budgetEditor.approveError": "Não foi possível aprovar",
    "budgetEditor.saveBeforeCreateWork": "Guarde o orçamento antes de criar a obra",
    "budgetEditor.executionNeedsPersistedBudget": "A execução precisa de um orçamento persistido.",
    "budgetEditor.workCreated": "Obra criada",
    "budgetEditor.workAvailableInMenu": "O orçamento aprovado já aparece no menu Obras.",
    "budgetEditor.createWorkError": "Não foi possível criar a obra",
    "budgetEditor.statusUpdated": "Estado atualizado",
    "budgetEditor.statusUpdateError": "Não foi possível atualizar o estado",
    "budgetEditor.loading": "A carregar editor de orçamento...",
    "budgetEditor.autoSaveBanner": "Falha no guardado automático. As alterações continuam no editor, mas ainda não foram gravadas. {saveError}",
    "budgetEditor.previewPdf": "Pré-visualizar PDF",
    "budgetEditor.generateEmail": "Gerar email",
    "budgetEditor.createWork": "Criar Obra",
    "budgetEditor.actionNotWiredDescription": "Ação pronta na UI; integração fica para o coordenador ou Worker E.",
    
    // Assumptions Step
    "assumptions.title": "Pressupostos Técnicos",
    "assumptions.subtitle": "Defina as condições e pressupostos em que o orçamento se baseia",
    "assumptions.generate": "Gerar Pressupostos",
    "assumptions.add": "Adicionar Pressuposto",
    "assumptions.placeholder": "Descreva o pressuposto...",
    "assumptions.visibleToClient": "Visível para cliente",
    "assumptions.internalOnly": "Apenas interno",
    "assumptions.count": "pressupostos",
    "assumptions.visible": "visíveis",
    "assumptions.internal": "internos",
    "assumptions.defaultProjectType": "Orçamentação técnica de construção",
    
    // Review Step
    "review.title": "Revisão do Orçamento",
    "review.subtitle": "Reveja todos os detalhes antes de guardar",
    "review.verification": "Verificação",
    "review.internalView": "Vista Interna",
    "review.clientView": "Vista Cliente",
    "review.identification": "Identificação",
    "review.project": "Projeto",
    "review.type": "Tipo",
    "review.location": "Localização",
    "review.client": "Cliente",
    "review.definition": "Definição",
    "review.scope": "Âmbito",
    "review.services": "serviços",
    "review.items": "itens",
    "review.financialSummary": "Resumo Financeiro",
    "review.servicesIncluded": "Serviços Incluídos",
    "review.itemsIncluded": "Itens Incluídos",
    "review.totalValue": "Valor Total",
    "review.vatNotIncluded": "IVA não incluído",
    "tax.name.pt": "IVA",
    "tax.name.es": "IVA",
    "tax.name.fr": "TVA",
    "tax.name.uk": "VAT",
    "tax.name.default": "Tax",
    "tax.showWithTax": "Mostrar valores com imposto?",
    "tax.withoutTax": "Total sem imposto",
    "tax.percentage": "Percentagem de imposto",
    "tax.value": "Valor do imposto",
    "tax.totalWithTax": "Total com imposto",
    "tax.reverseCharge": "Autoliquidação",
    "tax.reverseChargeHint": "Aplicável ao setor da construção",
    "tax.reverseChargeNote": "Aplicação do regime de autoliquidação do IVA nos termos da legislação em vigor. O IVA será liquidado pelo adquirente.",
    "tax.unusualWarning": "Valor incomum",
    "variations.createAdditional": "Criar Trabalho a Mais",
    "variations.onlyAfterSave": "Os trabalhos a mais só podem ser criados após guardar o orçamento principal.",
    "variations.referenceBudget": "Referência ao orçamento",
    
    // Variation Orders
    "variations.title": "Trabalhos a Mais",
    "variations.subtitle": "Trabalhos adicionais ao orçamento principal",
    "variations.add": "Adicionar Trabalho a Mais",
    "variations.number": "Nº",
    "variations.description": "Descrição",
    "variations.value": "Valor",
    "variations.total": "Total Trabalhos a Mais",
    "variations.exportSeparate": "Exportar Separadamente",
    "variations.exportAsAnnex": "Exportar como Anexo",
    
    // Execution Time
    "executionTime.title": "Prazo de Execução Estimado",
    "executionTime.aiSuggest": "Sugerir Prazo",
    "executionTime.days": "dias úteis",
    "executionTime.range": "Intervalo estimado",
    "executionTime.disclaimer": "Duração indicativa, sujeita a condições do local",
    "executionTime.visibleToClient": "Mostrar ao cliente",
    "executionTime.internalOnly": "Apenas interno",
    
    // Services
    "services.architecture": "Projeto de Arquitetura",
    "services.structural": "Projeto de Estabilidade",
    "services.hydraulic": "Projeto de Redes Hidráulicas",
    "services.electrical": "Projeto de Instalações Elétricas",
    "services.hvac": "Projeto de AVAC",
    "services.gas": "Projeto de Gás",
    "services.fire": "Projeto de Segurança Contra Incêndio",
    "services.acoustic": "Projeto de Condicionamento Acústico",
    "services.thermal": "Projeto de Comportamento Térmico",
    "services.telecom": "Projeto de Telecomunicações (ITED)",
    "services.coordination": "Coordenação de Projeto",
    "services.supervision": "Fiscalização de Obra",
    
    // Settings
    "settings.title": "Definições da Empresa",
    "settings.subtitle": "Configure a informação da sua empresa para os documentos",
    "settings.companyInfo": "Informação da Empresa",
    "settings.companyInfoDesc": "Estes dados aparecem nos PDFs e documentos gerados",
    "settings.companyName": "Nome da empresa",
    "settings.companyNamePlaceholder": "Ex: Engenharia Silva, Lda.",
    "settings.logo": "Logótipo",
    "settings.logoHint": "PNG ou JPG, máx. 5MB. Aparece no canto superior esquerdo do PDF.",
    "settings.address": "Morada completa",
    "settings.addressPlaceholder": "Ex: Rua da Engenharia, nº 10",
    "settings.postalCode": "Código Postal",
    "settings.country": "País",
    "settings.language": "Idioma",
    "settings.currency": "Moeda",
    "settings.phone": "Telefone",
    "settings.email": "Email",
    "settings.website": "Website",
    "settings.taxId": "NIF / VAT ID",
    "settings.licenseNumber": "Nº de Alvará",
    "settings.licenseNumberPlaceholder": "Opcional",
    "settings.technicalResponsible": "Responsável Técnico",
    "settings.technicalResponsiblePlaceholder": "Nome do responsável técnico",
    "settings.signature": "Assinatura",
    "settings.signatureDesc": "Imagem de assinatura para incluir nos documentos (opcional)",
    "settings.budgetNumbering": "Numeração de Orçamentos",
    "settings.budgetNumberingDesc": "Configure o prefixo para a numeração automática",
    "settings.prefix": "Prefixo",
    "settings.previewLabel": "Exemplo",
    "settings.pdfPreview": "Pré-visualização do Cabeçalho PDF",
    "settings.pdfPreviewDesc": "Assim aparecerá o cabeçalho nos seus documentos",
    "settings.pdfPreviewHint": "Pré-visualização do cabeçalho do documento",
    "settings.saved": "Definições guardadas com sucesso",
    "settings.invalidImageFormat": "Formato inválido. Use PNG ou JPG.",
    "settings.imageTooLarge": "Imagem demasiado grande. Máximo 5MB.",
    "dashboard.settings": "Definições",

    // IFC Import
    "ifcImport.upload.title": "Importar modelo IFC",
    "ifcImport.upload.subtitle": "Arraste um ficheiro .ifc ou selecione no computador. Depois da leitura, poderá criar um orçamento novo ou associar o modelo a um orçamento existente.",
    "ifcImport.upload.button": "Selecionar IFC",
    "ifcImport.upload.parsing": "A analisar IFC...",
    "ifcImport.summary.title": "IFC importado com sucesso",
    "ifcImport.summary.total": "elementos importados",
    "ifcImport.summary.assign": "Atribuir composições",
    "ifcImport.summary.warnings": "Avisos",
    "ifcImport.table.empty": "Ainda não existem elementos IFC importados.",
    "ifcImport.table.type": "Tipo IFC",
    "ifcImport.table.name": "Nome",
    "ifcImport.table.level": "Piso",
    "ifcImport.table.quantity": "Quantidade",
    "ifcImport.table.unit": "Unidade",
    "ifcImport.table.composition": "Composição",
    "ifcImport.table.unitCost": "Custo unitário",
    "ifcImport.table.assign": "Atribuir",
    "ifcImport.mapper.title": "Mapeamento de composições",
    "ifcImport.mapper.generate": "Gerar itens de obra",
    "ifcImport.success.persisted": "Elementos IFC guardados",
    "ifcImport.error.invalidFile": "Selecione um ficheiro .ifc válido.",
    "ifcImport.error.fileTooLarge": "O ficheiro IFC excede o limite de 100MB.",
    "ifcImport.error.tooLarge": "O ficheiro IFC excede o limite de 100MB.",
    "ifcImport.error.tooSmall": "O ficheiro IFC e demasiado pequeno para ser valido.",
    "ifcImport.error.notIfc": "Selecione um ficheiro IFC .ifc valido. Variantes comprimidas nao sao aceites.",
    "ifcImport.error.budgetForbidden": "O orcamento selecionado nao pertence a organizacao ativa.",
    "ifcImport.error.invalidMetadata": "Os metadados do ficheiro IFC sao invalidos.",
    "ifcImport.error.uploadInitFailed": "Nao foi possivel iniciar a importacao IFC.",
    "ifcImport.error.invalidIfcHeader": "O ficheiro nao contem um cabecalho STEP IFC valido (ISO-10303-21;).",
    "ifcImport.error.generic": "Não foi possível analisar o ficheiro IFC.",
    "ifcImport.error.readFile": "Não foi possível ler o ficheiro.",
    "ifcImport.error.persist": "Não foi possível guardar os elementos IFC.",
    "ifcImport.error.assign": "Não foi possível atribuir a composição.",
    "ifcImport.error.authRequired": "Inicie sessão para importar IFC.",
    "ifcImport.error.noOrganization": "Organização não encontrada.",
    "ifcImport.error.loadCompositions": "Não foi possível carregar composições.",
    "ifcImport.warning.emptyFile": "O ficheiro IFC está vazio.",
    "ifcImport.warning.noStepRecords": "Não foram encontrados registos STEP no ficheiro.",
    "ifcImport.warning.noElements": "Não foram encontrados elementos construtivos suportados.",
    "ifcImport.warning.largeModel": "Modelo com mais de 5000 elementos; a revisão pode ficar lenta.",
    "compositionPicker.placeholder": "Selecionar composição",
    "compositionPicker.search": "Pesquisar por nome ou código...",
    "compositionPicker.empty": "Nenhuma composição encontrada.",
    "compositionPicker.clear": "Limpar composição",

    // BOQ
    "boq.export.menu": "Exportar BOQ / XLSX",
    "boq.export.button": "Exportar BOQ",
    "boq.export.download": "Download XLSX",
    "boq.dialog.title": "Mapa de Quantidades",
    "boq.table.description": "Descrição",
    "boq.table.unit": "Un.",
    "boq.table.quantity": "Qtd.",
    "boq.table.unitCost": "P.U. €",
    "boq.table.total": "Total €",
    "boq.summary.risk": "Risco",
    "boq.summary.costWithRisk": "Custo + Risco",
    "boq.summary.margin": "Margem",
    "boq.section.manual": "Manual",
    "boq.pdf.title": "Mapa de Quantidades",
    "boq.pdf.toggle": "Incluir Mapa de Quantidades",
    "budgetReview.rule.noZeroUnitPrice": "Todos os itens diretos devem ter preço > 0",
    "budgetReview.rule.noZeroQuantity": "Todos os itens devem ter quantidade > 0",
    "budgetReview.rule.unmappedIfc": "Elementos IFC sem composição atribuída",
    "budgetReview.rule.noWorkItems": "O orçamento deve ter pelo menos um item de obra",
    "budgetReview.rule.noRiskMargin": "A margem de orçamento é 0%",
    "budgetReview.violation.missingUnitPrice": "Preço unitário em falta",
    "budgetReview.violation.zeroQuantity": "Quantidade zero ou negativa",
    "budgetReview.violation.noComposition": "Sem composição atribuída",
    "budgetReview.violation.noWorkItems": "Nenhum item de obra definido",
    "budgetReview.violation.noMargin": "Considere adicionar uma margem de contingência",
    "budgetReview.badge.complete": "Completo",
    "budgetReview.badge.errors": "{{count}} erro(s)",
    "budgetReview.badge.warnings": "{{count}} aviso(s)",
    "budgetReview.panel.title": "Análise do Orçamento",
    "budgetReview.panel.passed": "regras verificadas",
    "budgetReview.panel.override": "Exportar mesmo assim",
    "ifcImport.wizard.button": "Importar IFC",
    "ifcImport.wizard.sheetTitle": "Importar de ficheiro IFC",
    "ifcImport.wizard.success": "{{count}} itens de obra gerados",
    "ifcImport.wizard.saveFirst": "Guarde o orcamento antes de importar IFC.",
    
    // Reminders & Deadlines
    "reminders.title": "Lembretes",
    "reminders.popup.title": "Lembretes Pendentes",
    "reminders.popup.dismiss": "Dispensar",
    "reminders.popup.dismissAll": "Dispensar Todos",
    "reminders.sentReminder": "Orçamento enviado há {days} dias sem resposta",
    "reminders.draftDeadline": "Prazo interno do rascunho expira em {days} dias",
    "reminders.draftDeadlineExpired": "Prazo interno do rascunho expirou",
    "reminders.configDays": "Lembrar após (dias)",
    "reminders.draftDeadlineLabel": "Prazo interno (opcional)",
    "reminders.noPending": "Sem lembretes pendentes",
    
    // PDF
    "pdf.export": "Exportar PDF",
    "pdf.clientView": "PDF Cliente",
    "pdf.internalView": "PDF Interno",
    "pdf.generating": "A gerar PDF...",
    "pdf.technicalNotes": "Notas Técnicas e Condições do Orçamento",
    "pdf.premiumRequired": "Funcionalidade Premium",
    "pdf.premiumDesc": "Idioma do documento, temas, logótipo, marca de água e página de notas técnicas estão disponíveis no plano Premium.",
    "pdf.exportLanguage": "Idioma do Documento",
    "pdf.watermark": "Marca de água",
    "pdf.watermarkOpacity": "Opacidade",
    "pdf.theme": "Tema do PDF",
    "pdf.theme.neutral": "Neutro",
    "pdf.theme.professional": "Profissional",
    "pdf.theme.modern": "Moderno",
    "pdf.boq.chapterSummary": "Resumo por Capítulo",
    "pdf.commercialConditions": "Condições Comerciais",
    "pdf.commercialConditions.validUntil": "Validade da Proposta",
    "pdf.commercialConditions.paymentTerms": "Condições de Pagamento",
    "pdf.commercialConditions.executionDeadline": "Prazo de Execução",
    "pdf.commercialConditions.expressExclusions": "Exclusões Expressas",
    "pdf.signature.title": "Concordância e Assinatura",
    "pdf.signature.date": "Local e Data",
    "pdf.signature.company": "Empresa / Responsável",
    "pdf.signature.stamp": "Assinatura e Carimbo",
    "pdf.popupBlockedFallbackTitle": "Pop-up bloqueado",
    "pdf.popupBlockedFallbackDescription": "Foi descarregado um ficheiro HTML imprimível para continuar a exportação.",
    "pdf.executiveSummary": "Resumo Executivo",
    "pdf.proposedValue": "Valor Proposto",
    "pdf.subtotal": "Subtotal",
    "pdf.totalWithVat": "Total com IVA",
    "pdf.reverseCharge": "Inversão do sujeito passivo",
    "pdf.internalDocument": "DOCUMENTO INTERNO",
    "pdf.internalDocWarning": "Este documento é de uso exclusivamente interno e não deve ser partilhado com clientes.",
    "pdf.status.draft": "RASCUNHO",
    "pdf.status.sent": "ENVIADO",
    "pdf.status.approved": "APROVADO",
    "pdf.status.rejected": "REJEITADO",
    "pdf.status.expired": "EXPIRADO",
    "pdf.signature.declarationText": "Declaro que tomei conhecimento do presente orçamento e concordo com as condições nele constantes.",
    "pdf.signature.client": "Cliente",
    "pdf.proposalValidUntil": "Proposta válida até",
    "pdf.exclusions": "Exclusões",
    "onboarding.locale.title": "Configuração inicial",
    "onboarding.locale.description": "Defina país, idioma e moeda antes de continuar para o dashboard.",
    "onboarding.locale.countryPlaceholder": "Selecionar país",
    "onboarding.locale.languagePlaceholder": "Selecionar idioma",
    "onboarding.locale.currencyPlaceholder": "Selecionar moeda",
    "onboarding.locale.continue": "Guardar e continuar",
    "onboarding.locale.savedTitle": "Preferências guardadas",
    "onboarding.locale.savedDescription": "País, idioma e moeda definidos para o workspace.",
    "onboarding.locale.saveError": "Não foi possível guardar as preferências iniciais.",
    "ai.chat.aria": "Assistente Orzio",
    "ai.chat.title": "Assistente Orzio",
    "ai.chat.welcome": "Olá. Como posso ajudar com o teu orçamento?",
    "ai.chat.thinking": "A pensar...",
    "ai.chat.placeholder": "Escreve uma mensagem...",
    "ai.chat.hint": "Enter para enviar · Shift+Enter nova linha",
    "ai.chat.error": "Erro ao contactar assistente.",
    "ai.chat.context.budget": "Orçamento em contexto",
    "ai.chat.context.status": "Estado",
    "ai.bulkDescribe.title": "Confirmar descrições geradas",
    "ai.bulkDescribe.description": "Reveja as sugestões antes de atualizar as partidas importadas do modelo.",
    "ai.bulkDescribe.current": "Descrição atual",
    "ai.bulkDescribe.suggested": "Sugestão da IA",
    "ai.bulkDescribe.empty": "Sem descrição definida",
    "ai.bulkDescribe.apply": "Aplicar descrições",
    "ai.proposal.title": "Gerar email de proposta",
    "ai.proposal.description": "Revise o assunto e o corpo do email antes de copiar.",
    "ai.proposal.subject": "Assunto",
    "ai.proposal.body": "Corpo do email",
    "ai.proposal.copy": "Copiar email",
    "ai.proposal.copied": "Email copiado para a área de transferência.",
  },
  en: {
    // Common
    "app.name": "Orzio",
    "common.save": "Save",
    "common.cancel": "Cancel",
    "common.next": "Next",
    "common.previous": "Previous",
    "common.delete": "Delete",
    "common.edit": "Edit",
    "common.duplicate": "Duplicate",
    "common.export": "Export",
    "common.add": "Add",
    "common.remove": "Remove",
    "common.loading": "Loading...",
    "common.close": "Close",
    "common.retry": "Retry",
    "common.error": "Error",
    "common.success": "Success",
    "common.total": "Total",
    "common.subtotal": "Subtotal",
    "common.unit": "Unit",
    "common.quantity": "Quantity",
    "common.item": "Item",
    "common.price": "Price",
    "common.description": "Description",
    "common.observations": "Observations",
    "common.conditions": "Conditions",
    "common.step": "Step",
    "common.of": "of",

    // Error boundary
    "errorBoundary.title": "The application encountered an unexpected error.",
    "errorBoundary.description": "You can try again, reload the page, or return home. If the problem continues, contact support with the reference below.",
    "errorBoundary.reference": "Reference:",
    "errorBoundary.retry": "Try again",
    "errorBoundary.reload": "Reload page",
    "errorBoundary.goHome": "Return home",
    "errorBoundary.contactSupport": "Contact support",
    "errorBoundary.devDetails": "Technical details (dev)",
    "errorBoundary.mail.subject": "[Orzio] Unexpected error{eventIdSuffix}",
    "errorBoundary.mail.greeting": "Hello Orzio team,",
    "errorBoundary.mail.intro": "An unexpected error occurred in the application.",
    "errorBoundary.mail.reference": "Internal reference: {eventId}",
    "errorBoundary.mail.message": "Message: {message}",
    "errorBoundary.mail.url": "URL: {url}",
    "errorBoundary.mail.userAction": "Description of what I was doing:",
    
    // Auth
    "auth.login": "Sign In",
    "auth.signup": "Sign Up",
    "auth.logout": "Sign Out",
    "auth.email": "Email",
    "auth.password": "Password",
    "auth.emailPlaceholder": "engineer@example.com",
    "auth.productTagline": "Technical estimating for construction",
    "auth.signupTitle": "Create Account",
    "auth.loginTitle": "Sign In",
    "auth.signupDesc": "Create your account to start structuring budgets",
    "auth.loginDesc": "Sign in to your account to continue",
    "auth.loginWithGoogle": "Sign in with Google",
    "auth.signupWithGoogle": "Sign up with Google",
    "auth.modeSelector": "Choose authentication mode",
    "auth.modeCurrent": "Current mode",
    "auth.switchToLogin": "Switch to sign in",
    "auth.switchToSignup": "Switch to create account",
    "auth.orEmail": "or email",
    "auth.forgotPassword": "Forgot password?",
    "auth.resetTitle": "Recover password",
    "auth.resetDesc": "Enter your account email and we will send a recovery link.",
    "auth.sendReset": "Send recovery email",
    "auth.resetEmailSent": "Email sent",
    "auth.resetEmailDesc": "Check your inbox to set a new password.",
    "auth.resetError": "Could not process password recovery.",
    "auth.newPasswordTitle": "Set new password",
    "auth.newPasswordDesc": "Choose a new password to continue using Orzio.",
    "auth.newPassword": "New password",
    "auth.confirmPassword": "Confirm new password",
    "auth.passwordMismatch": "Passwords do not match.",
    "auth.saveNewPassword": "Save new password",
    "auth.passwordUpdated": "Password updated",
    "auth.passwordUpdatedDesc": "You can continue using Orzio with the new password.",
    "auth.recoveryChecking": "Validating your recovery link...",
    "auth.recoveryLinkInvalid": "This recovery link is invalid or expired. Request a new password reset email.",
    "auth.backToLogin": "Back to sign in",
    "auth.googleError": "Could not sign in with Google.",
    "auth.hasAccount": "Already have an account?",
    "auth.noAccount": "Don't have an account?",
    "auth.accountCreated": "Account created",
    "auth.checkEmail": "Check your email to confirm your account.",
    "auth.invalidCredentials": "Invalid email or password.",
    "auth.emailExists": "This email is already registered.",
    "auth.invalidEmail": "Invalid email",
    "auth.passwordTooShort": "Password must be at least 6 characters",
    
    // Landing
    "landing.hero.title": "Structured Technical Budgets",
    "landing.hero.subtitle": "Professional tool for engineers and technical offices. Structure, justify, and standardize your engineering budgets.",
    "landing.cta.start": "Get Started",
    "landing.cta.demo": "View Demo",
    "landing.cta.create": "Create Free Account",
    "landing.principles.title": "Core Principles",
    "landing.principles.deterministic.title": "Deterministic Calculations",
    "landing.principles.deterministic.desc": "All values are calculated with precise logic. No AI estimates for critical numbers.",
    "landing.principles.structured.title": "Structured Workflow",
    "landing.principles.structured.desc": "Guided process in mandatory steps. Ensure no essential element is left out.",
    "landing.principles.separation.title": "Clear Separation",
    "landing.principles.separation.desc": "Distinction between internal technical data and client-visible information.",
    "landing.features.title": "Features",
    "landing.features.guided": "Guided technical budget creation",
    "landing.features.costs": "Cost block and labor management",
    "landing.features.risk": "Automatic risk and margin calculation",
    "landing.features.assumptions": "Structured technical assumptions",
    "landing.features.pdf": "Professional PDF export",
    "landing.features.versions": "Version history",
    "landing.features.ai": "AI-assisted scope suggestions",
    "landing.features.gdpr": "PDF with separate internal and client views",
    "landing.features.projects": "Project Development Budgets",
    "landing.features.works": "Works Execution Budgets",
    "landing.cta.title": "Professionalize your budgets",
    "landing.cta.desc": "Join engineers and technical offices already structuring their budgets professionally.",
    "landing.footer": "European Context. All rights reserved.",
    
    // Dashboard
    "dashboard.title": "Budgets",
    "dashboard.subtitle": "Manage and create technical budgets",
    "dashboard.newBudget": "New Budget",
    "dashboard.stats.total": "Total Budgets",
    "dashboard.stats.drafts": "Drafts",
    "dashboard.stats.sent": "Sent",
    "dashboard.stats.approved": "Approved",
    "dashboard.stats.rejected": "Rejected",
    "dashboard.stats.expired": "Expired",
    "dashboard.filters": "Filters",
    "dashboard.allBudgets": "All",
    "dashboard.list.title": "Budget List",
    "dashboard.list.version": "Version",
    "dashboard.list.totalPrice": "Total Price",
    "dashboard.status.draft": "Draft",
    "dashboard.status.sent": "Sent",
    "dashboard.status.approved": "Approved",
    "dashboard.status.rejected": "Rejected",
    "dashboard.status.expired": "Expired",
    "dashboard.approvalValue": "Final agreed value?",
    "dashboard.discountApplied": "Discount applied",
    "dashboard.reopen": "Reopen",
    "dashboard.validity": "Validity",
    "dashboard.validityDays": "days validity",
    "dashboard.exportPdf": "Export PDF",
    "dashboard.markSent": "Mark as Sent",
    "dashboard.markApproved": "Mark as Approved",
    "dashboard.markRejected": "Mark as Rejected",
    "dashboard.reopenBudget": "Reopen Budget",
    "dashboard.approveTitle": "Approve Budget",
    "dashboard.approveDesc": "Enter the final agreed value with the client.",
    "dashboard.agreedValue": "Agreed value",
    "dashboard.unplannedDiscount": "Unplanned discount",
    "dashboard.confirm": "Confirm",
    "dashboard.statusChanged": "Status changed successfully",
    "dashboard.expired.auto": "Automatically expired",
    "dashboard.preSendReview.title": "Review before sending",
    "dashboard.preSendReview.description": "Orzio found a few points worth checking before marking the budget as sent.",
    "dashboard.preSendReview.continue": "Send anyway",
    "dashboard.preSendReview.back": "Back to budget",
    "dashboard.preSendReview.summary": "Summary",
    
    // Budget Types
    "budgetType.title": "Budget Type",
    "budgetType.subtitle": "Choose the type of budget you want to create",
    "budgetType.projects.title": "Project Development",
    "budgetType.projects.desc": "Budgets for engineering services and technical projects. Based on hours and specialties.",
    "budgetType.works.title": "Works Execution",
    "budgetType.works.desc": "Budgets for construction and contracting. Based on items, quantities, and unit prices.",
    "budgetType.unavailable": "Not included in plan",
    "budgetType.warning": "This choice cannot be changed after budget creation.",
    
    // Wizard Steps
    "wizard.step.type": "Type",
    "wizard.step.identification": "Identification",
    "wizard.step.scope": "Scope",
    "wizard.step.costs": "Costs",
    "wizard.step.risk": "Risk",
    "wizard.step.assumptions": "Assumptions",
    "wizard.step.executionTime": "Timeline",
    "wizard.step.review": "Review",
    "wizard.newBudget": "New Budget",
    "wizard.saveBudget": "Save Budget",
    "wizard.saving": "Saving...",
    "wizard.saved": "Budget saved",
    "wizard.savedDesc": "Your budget has been saved successfully.",
    
    // Identification Step
    "identification.title": "Project Identification",
    "identification.subtitle": "Basic information about the project to budget",
    "identification.projectName": "Project Name",
    "identification.projectNamePlaceholder": "E.g.: Tejo Residential Building",
    "identification.projectType": "Project Type",
    "identification.projectType.newBuild": "New Construction",
    "identification.projectType.rehabilitation": "Rehabilitation",
    "identification.projectType.industrial": "Industrial",
    "identification.projectType.technicalService": "Technical Service",
    "identification.projectType.other": "Other",
    "identification.projectType.otherPlaceholder": "Specify the project type...",
    "identification.selectType": "Select type",
    "identification.selectLevel": "Select level",
    "identification.location": "Location",
    "identification.locationPlaceholder": "E.g.: Lisbon, Portugal",
    "identification.clientType": "Client Type",
    "identification.clientType.private": "Private",
    "identification.clientType.company": "Company",
    "identification.clientType.public": "Public Entity",
    "identification.definitionLevel": "Project Definition Level",
    "identification.definitionLevel.low": "Low (Idea/Concept)",
    "identification.definitionLevel.medium": "Medium (Preliminary Study)",
    "identification.definitionLevel.high": "High (Base/Execution Project)",
    "identification.definitionLevelHint": "The lower the definition, the higher the associated risk",
    
    // Scope Step (Projects)
    "scope.projects.title": "Technical Scope",
    "scope.projects.subtitle": "Select the engineering services included in this budget",
    "scope.projects.suggest": "Suggest Scope",
    "scope.projects.additionalScope": "Additional Scope",
    "scope.projects.additionalScopePlaceholder": "Describe additional services not listed above...",
    "scope.projects.selected": "service(s) selected",
    "scope.projects.selectAtLeastOne": "Select at least one service to continue",
    
    // Scope Step (Works)
    "scope.works.title": "Works Scope",
    "scope.works.subtitle": "Define the typology and general conditions of the works",
    "scope.works.typology": "Works Typology",
    "scope.works.typology.construcao_nova": "New Construction",
    "scope.works.typology.reabilitacao": "Rehabilitation / Renovation",
    "scope.works.typology.industrial_l1": "Industrial",
    "scope.works.typology.obra_parcial": "Partial / Specialized Works",
    "scope.works.typology.infraestrutura": "Infrastructure",
    "scope.works.typology.instalacoes_tecnicas": "Technical Installations",
    "scope.works.typology.manutencao": "Maintenance / Conservation",
    "scope.works.typology.outra": "Other",
    "scope.works.typology.outraPlaceholder": "Specify the typology...",
    "scope.works.subTypologyLabel": "Sub-typology (optional)",
    "scope.works.sub.habitacao": "Residential",
    "scope.works.sub.comercial": "Commercial",
    "scope.works.sub.misto": "Mixed-use",
    "scope.works.sub.edificio_publico": "Public Building",
    "scope.works.sub.estrutural": "Structural",
    "scope.works.sub.interior": "Interior",
    "scope.works.sub.fachadas": "Facades",
    "scope.works.sub.integral": "Full Renovation",
    "scope.works.sub.nave": "Industrial Warehouse",
    "scope.works.sub.plataformas": "Platforms",
    "scope.works.sub.instalacoes": "Installations",
    "scope.works.sub.estrutura_metalica": "Steel Structure",
    "scope.works.sub.coberturas": "Roofing",
    "scope.works.sub.fachadas_parcial": "Facades",
    "scope.works.sub.impermeabilizacao": "Waterproofing",
    "scope.works.sub.arruamentos": "Road Works",
    "scope.works.sub.saneamento": "Sewage",
    "scope.works.sub.abastecimento": "Water Supply",
    "scope.works.sub.avac": "HVAC",
    "scope.works.sub.eletricidade": "Electrical",
    "scope.works.sub.telecomunicacoes": "Telecommunications",
    "scope.works.sub.gas": "Gas",
    "scope.works.sub.preventiva": "Preventive",
    "scope.works.sub.corretiva": "Corrective",
    "scope.works.sub.conservacao": "Conservation",
    "scope.works.sub.other": "Other",
    "scope.works.sub.otherPlaceholder": "Specify...",
    "scope.works.selectionsCount": "selection(s)",
    "scope.works.generalObservations": "General Observations",
    "scope.works.generalObservationsPlaceholder": "Describe the general conditions of the works...",
    "scope.works.exclusions": "Conditions and Exclusions",
    "scope.works.exclusionsPlaceholder": "List the conditions and exclusions...",
    
    // Cost Blocks Step (Projects)
    "costs.projects.title": "Cost Blocks",
    "costs.projects.subtitle": "Define labor and other associated costs",
    "costs.projects.labor": "Labor",
    "costs.projects.indirect": "Indirect Costs",
    "costs.projects.addLine": "Add Line",
    "costs.projects.addBlock": "Add Cost Block",
    "costs.projects.role": "Role",
    "costs.projects.hours": "Hours",
    "costs.projects.hourlyRate": "€/Hour",
    "costs.projects.baseCost": "Total Base Cost",
    "costs.projects.beforeRiskMargin": "Before risk and margin",
    "costs.projects.roles.seniorEngineer": "Senior Engineer",
    "costs.projects.roles.engineer": "Engineer",
    "costs.projects.roles.juniorEngineer": "Junior Engineer",
    "costs.projects.roles.technician": "Technician",
    "costs.projects.roles.draftsman": "Draftsman",
    "costs.projects.roles.admin": "Administrative",
    
    // Cost Items Step (Works)
    "costs.works.title": "Bill of Quantities",
    "costs.works.subtitle": "Define work items with quantities and unit prices",
    "costs.works.import.title": "Import Orzio Takeoff quantities",
    "costs.works.import.subtitle": "Accepts .xlsx files emitted by the Orzio Takeoff Add-in for Revit with a valid technical schema.",
    "costs.works.import.button": "Import XLSX",
    "costs.works.import.itemsImported": "items imported from",
    "costs.works.import.genericError": "Could not import the Orzio Takeoff file.",
    "costs.works.addItem": "Add Item",
    "costs.works.itemDescription": "Item Description",
    "costs.works.itemDescriptionPlaceholder": "E.g.: Supply and assembly of steel structure...",
    "costs.works.unitPrice": "Unit Price",
    "costs.works.totalPrice": "Total Price",
    "costs.works.composition": "Composition",
    "costs.works.openComposition": "Open Composition",
    "costs.works.closeComposition": "Close Composition",
    "costs.works.addSubItem": "Add Sub-item",
    "costs.works.subItem.labor": "Labor",
    "costs.works.subItem.materials": "Materials",
    "costs.works.subItem.equipment": "Equipment",
    "costs.works.subItem.other": "Other",
    "costs.works.referencePrice": "Reference Price",
    "costs.works.referencePriceHint": "Optional - used as guidance only",
    "costs.works.units.m2": "m²",
    "costs.works.units.ml": "ml",
    "costs.works.units.kg": "kg",
    "costs.works.units.un": "un",
    "costs.works.units.h": "h",
    "costs.works.units.vg": "ls",
    "costs.works.units.m": "m",
    "costs.works.units.m3": "m³",
    "costs.works.units.t": "t",
    "costs.works.units.day": "day",
    "costs.works.units.week": "week",
    "costs.works.units.lot": "lot",
    "costs.works.units.percent": "%",
    "costs.works.units.global": "global",
    "costs.works.totalBase": "Base Total",
    
    // Risk & Margin Step
    "risk.title": "Risk and Margin",
    "risk.subtitle": "Define the risk level and desired profit margin",
    "risk.level": "Project Risk Level",
    "risk.level.low": "Low",
    "risk.level.low.desc": "Well-defined project, experienced client, known conditions",
    "risk.level.medium": "Medium",
    "risk.level.medium.desc": "Partial definition, some technical or scope uncertainties",
    "risk.level.high": "High",
    "risk.level.high.desc": "Project with many unknowns, inexperienced client, tight deadlines",
    "risk.customPercentage": "Custom Risk Percentage",
    "risk.margin": "Profit Margin",
    "risk.marginBelowMin": "Margin below organization minimum",
    "risk.indirectCosts": "Indirect Costs",
    "risk.indirectCostsHint": "Overhead, administration, etc.",
    "risk.calculation": "Real-time Calculation",
    "risk.baseCost": "Base Cost",
    "risk.riskProvision": "Risk Provision",
    "risk.costWithRisk": "Cost with Risk",
    "risk.marginValue": "Margin",
    "risk.finalPrice": "Final Price",
    "risk.marginBelowMinWithValue": "Margin below the organization minimum ({min}%).",
    "risk.ai.title": "AI margin suggestion",
    "risk.ai.loading": "Analyzing history and commercial context...",
    "risk.ai.sampleConfidence": "Sample: {sampleSize} budget(s) · Confidence {confidence}",
    "risk.ai.apply": "Apply {margin}%",
    "risk.ai.error": "Could not suggest the margin.",
    "risk.ai.confidence.high": "High",
    "risk.ai.confidence.medium": "Medium",
    "risk.ai.confidence.low": "Low",

    // Budget editor
    "budgetEditor.unknownError": "Unknown error.",
    "budgetEditor.createError": "Could not create the budget",
    "budgetEditor.createErrorWithMessage": "Could not create the budget: {message}",
    "budgetEditor.snapshotLoadError": "Could not load the snapshot. Showing demo data.",
    "budgetEditor.demoMode": "Editor in demo mode",
    "budgetEditor.loadBudgetFailure": "Failed to load the budget.",
    "budgetEditor.autoSaveError": "Could not auto-save",
    "budgetEditor.manualSaveSuccess": "Budget saved",
    "budgetEditor.manualSaveSuccessDescription": "The changes were saved successfully.",
    "budgetEditor.manualSaveError": "Could not save",
    "budgetEditor.approvedLocally": "Budget approved locally",
    "budgetEditor.saveToOpenWork": "Create or save the budget to open the work.",
    "budgetEditor.approved": "Budget approved",
    "budgetEditor.approveError": "Could not approve",
    "budgetEditor.saveBeforeCreateWork": "Save the budget before creating the work",
    "budgetEditor.executionNeedsPersistedBudget": "Execution requires a persisted budget.",
    "budgetEditor.workCreated": "Work created",
    "budgetEditor.workAvailableInMenu": "The approved budget already appears in the Works menu.",
    "budgetEditor.createWorkError": "Could not create the work",
    "budgetEditor.statusUpdated": "Status updated",
    "budgetEditor.statusUpdateError": "Could not update the status",
    "budgetEditor.loading": "Loading budget editor...",
    "budgetEditor.autoSaveBanner": "Auto-save failed. Changes remain in the editor, but they have not been saved yet. {saveError}",
    "budgetEditor.previewPdf": "Preview PDF",
    "budgetEditor.generateEmail": "Generate email",
    "budgetEditor.createWork": "Create Work",
    "budgetEditor.actionNotWiredDescription": "Action ready in the UI; integration is left for the coordinator or Worker E.",
    
    // Assumptions Step
    "assumptions.title": "Technical Assumptions",
    "assumptions.subtitle": "Define the conditions and assumptions the budget is based on",
    "assumptions.generate": "Generate Assumptions",
    "assumptions.add": "Add Assumption",
    "assumptions.placeholder": "Describe the assumption...",
    "assumptions.visibleToClient": "Visible to client",
    "assumptions.internalOnly": "Internal only",
    "assumptions.count": "assumptions",
    "assumptions.visible": "visible",
    "assumptions.internal": "internal",
    "assumptions.defaultProjectType": "Technical construction estimating",
    
    // Review Step
    "review.title": "Budget Review",
    "review.subtitle": "Review all details before saving",
    "review.verification": "Verification",
    "review.internalView": "Internal View",
    "review.clientView": "Client View",
    "review.identification": "Identification",
    "review.project": "Project",
    "review.type": "Type",
    "review.location": "Location",
    "review.client": "Client",
    "review.definition": "Definition",
    "review.scope": "Scope",
    "review.services": "services",
    "review.items": "items",
    "review.financialSummary": "Financial Summary",
    "review.servicesIncluded": "Services Included",
    "review.itemsIncluded": "Items Included",
    "review.totalValue": "Total Value",
    "review.vatNotIncluded": "VAT not included",
    "tax.name.pt": "VAT",
    "tax.name.es": "VAT",
    "tax.name.fr": "VAT",
    "tax.name.uk": "VAT",
    "tax.name.default": "Tax",
    "tax.showWithTax": "Show values with tax?",
    "tax.withoutTax": "Total before tax",
    "tax.percentage": "Tax percentage",
    "tax.value": "Tax amount",
    "tax.totalWithTax": "Total with tax",
    "tax.reverseCharge": "Reverse charge",
    "tax.reverseChargeHint": "Applicable to the construction sector",
    "tax.unusualWarning": "Unusual value",
    "tax.reverseChargeNote": "Reverse charge mechanism applies under current legislation. VAT to be accounted for by the recipient.",
    "variations.createAdditional": "Create Variation Order",
    "variations.onlyAfterSave": "Variation orders can only be created after the main budget is saved.",
    "variations.referenceBudget": "Budget reference",
    
    // Variation Orders
    "variations.title": "Variation Orders",
    "variations.subtitle": "Additional works to the main budget",
    "variations.add": "Add Variation Order",
    "variations.number": "No.",
    "variations.description": "Description",
    "variations.value": "Value",
    "variations.total": "Total Variation Orders",
    "variations.exportSeparate": "Export Separately",
    "variations.exportAsAnnex": "Export as Annex",
    
    // Execution Time
    "executionTime.title": "Estimated Execution Time",
    "executionTime.aiSuggest": "Suggest Duration",
    "executionTime.days": "working days",
    "executionTime.range": "Estimated range",
    "executionTime.disclaimer": "Indicative duration, subject to site conditions",
    "executionTime.visibleToClient": "Show to client",
    "executionTime.internalOnly": "Internal only",
    
    // Services
    "services.architecture": "Architecture Project",
    "services.structural": "Structural Project",
    "services.hydraulic": "Hydraulic Networks Project",
    "services.electrical": "Electrical Installations Project",
    "services.hvac": "HVAC Project",
    "services.gas": "Gas Project",
    "services.fire": "Fire Safety Project",
    "services.acoustic": "Acoustic Conditioning Project",
    "services.thermal": "Thermal Behavior Project",
    "services.telecom": "Telecommunications Project (ITED)",
    "services.coordination": "Project Coordination",
    "services.supervision": "Works Supervision",
    
    // Settings
    "settings.title": "Company Settings",
    "settings.subtitle": "Configure your company information for documents",
    "settings.companyInfo": "Company Information",
    "settings.companyInfoDesc": "This data appears in generated PDFs and documents",
    "settings.companyName": "Company Name",
    "settings.companyNamePlaceholder": "E.g.: Silva Engineering, Ltd.",
    "settings.logo": "Logo",
    "settings.logoHint": "PNG or JPG, max 5MB. Appears top-left in PDF.",
    "settings.address": "Full Address",
    "settings.addressPlaceholder": "E.g.: 10 Engineering Street",
    "settings.postalCode": "Postal Code",
    "settings.country": "Country",
    "settings.language": "Language",
    "settings.currency": "Currency",
    "settings.phone": "Phone",
    "settings.email": "Email",
    "settings.website": "Website",
    "settings.taxId": "Tax ID / VAT Number",
    "settings.licenseNumber": "License Number",
    "settings.licenseNumberPlaceholder": "Optional",
    "settings.technicalResponsible": "Technical Responsible",
    "settings.technicalResponsiblePlaceholder": "Name of the technical responsible",
    "settings.signature": "Signature",
    "settings.signatureDesc": "Signature image for documents (optional)",
    "settings.budgetNumbering": "Budget Numbering",
    "settings.budgetNumberingDesc": "Configure the prefix for automatic numbering",
    "settings.prefix": "Prefix",
    "settings.previewLabel": "Example",
    "settings.pdfPreview": "PDF Header Preview",
    "settings.pdfPreviewDesc": "This is how your document header will look",
    "settings.pdfPreviewHint": "Document header preview",
    "settings.saved": "Settings saved successfully",
    "settings.invalidImageFormat": "Invalid format. Use PNG or JPG.",
    "settings.imageTooLarge": "Image too large. Maximum 5MB.",
    "dashboard.settings": "Settings",

    // IFC Import
    "ifcImport.upload.title": "Import IFC model",
    "ifcImport.upload.subtitle": "Drag a .ifc file here or select it from your computer. After parsing, you can create a new budget or link the model to an existing one.",
    "ifcImport.upload.button": "Select IFC",
    "ifcImport.upload.parsing": "Parsing IFC...",
    "ifcImport.summary.title": "IFC imported successfully",
    "ifcImport.summary.total": "imported elements",
    "ifcImport.summary.assign": "Assign compositions",
    "ifcImport.summary.warnings": "Warnings",
    "ifcImport.table.empty": "No IFC elements imported yet.",
    "ifcImport.table.type": "IFC type",
    "ifcImport.table.name": "Name",
    "ifcImport.table.level": "Level",
    "ifcImport.table.quantity": "Quantity",
    "ifcImport.table.unit": "Unit",
    "ifcImport.table.composition": "Composition",
    "ifcImport.table.unitCost": "Unit cost",
    "ifcImport.table.assign": "Assign",
    "ifcImport.mapper.title": "Composition mapping",
    "ifcImport.mapper.generate": "Generate work items",
    "ifcImport.success.persisted": "IFC elements saved",
    "ifcImport.error.invalidFile": "Select a valid .ifc file.",
    "ifcImport.error.fileTooLarge": "The IFC file exceeds the 100MB limit.",
    "ifcImport.error.tooLarge": "The IFC file exceeds the 100MB limit.",
    "ifcImport.error.tooSmall": "The IFC file is too small to be valid.",
    "ifcImport.error.notIfc": "Select a valid .ifc IFC file. Compressed variants are not accepted.",
    "ifcImport.error.budgetForbidden": "The selected budget does not belong to the active organization.",
    "ifcImport.error.invalidMetadata": "The IFC file metadata is invalid.",
    "ifcImport.error.uploadInitFailed": "Could not start the IFC import.",
    "ifcImport.error.invalidIfcHeader": "The file does not contain a valid IFC STEP header (ISO-10303-21;).",
    "ifcImport.error.generic": "Could not parse the IFC file.",
    "ifcImport.error.readFile": "Could not read the file.",
    "ifcImport.error.persist": "Could not save IFC elements.",
    "ifcImport.error.assign": "Could not assign the composition.",
    "ifcImport.error.authRequired": "Sign in to import IFC.",
    "ifcImport.error.noOrganization": "Organization not found.",
    "ifcImport.error.loadCompositions": "Could not load compositions.",
    "ifcImport.warning.emptyFile": "The IFC file is empty.",
    "ifcImport.warning.noStepRecords": "No STEP records were found in the file.",
    "ifcImport.warning.noElements": "No supported building elements were found.",
    "ifcImport.warning.largeModel": "Model has more than 5000 elements; review may be slower.",
    "compositionPicker.placeholder": "Select composition",
    "compositionPicker.search": "Search by name or code...",
    "compositionPicker.empty": "No compositions found.",
    "compositionPicker.clear": "Clear composition",

    // BOQ
    "boq.export.menu": "Export BOQ / XLSX",
    "boq.export.button": "Export BOQ",
    "boq.export.download": "Download XLSX",
    "boq.dialog.title": "Bill of Quantities",
    "boq.table.description": "Description",
    "boq.table.unit": "Unit",
    "boq.table.quantity": "Qty.",
    "boq.table.unitCost": "Unit €",
    "boq.table.total": "Total €",
    "boq.summary.risk": "Risk",
    "boq.summary.costWithRisk": "Cost + Risk",
    "boq.summary.margin": "Margin",
    "boq.section.manual": "Manual",
    "boq.pdf.title": "Bill of Quantities",
    "boq.pdf.toggle": "Include Bill of Quantities",
    "budgetReview.rule.noZeroUnitPrice": "All direct items must have a unit price > 0",
    "budgetReview.rule.noZeroQuantity": "All items must have quantity > 0",
    "budgetReview.rule.unmappedIfc": "IFC elements without assigned composition",
    "budgetReview.rule.noWorkItems": "The budget must have at least one work item",
    "budgetReview.rule.noRiskMargin": "The budget margin is 0%",
    "budgetReview.violation.missingUnitPrice": "Missing unit price",
    "budgetReview.violation.zeroQuantity": "Zero or negative quantity",
    "budgetReview.violation.noComposition": "No composition assigned",
    "budgetReview.violation.noWorkItems": "No work items defined",
    "budgetReview.violation.noMargin": "Consider adding a contingency margin",
    "budgetReview.badge.complete": "Complete",
    "budgetReview.badge.errors": "{{count}} error(s)",
    "budgetReview.badge.warnings": "{{count}} warning(s)",
    "budgetReview.panel.title": "Budget Review",
    "budgetReview.panel.passed": "rules passed",
    "budgetReview.panel.override": "Export anyway",
    "ifcImport.wizard.button": "Import IFC",
    "ifcImport.wizard.sheetTitle": "Import from IFC file",
    "ifcImport.wizard.success": "{{count}} work items generated",
    "ifcImport.wizard.saveFirst": "Save the budget before importing IFC.",
    
    // Reminders & Deadlines
    "reminders.title": "Reminders",
    "reminders.popup.title": "Pending Reminders",
    "reminders.popup.dismiss": "Dismiss",
    "reminders.popup.dismissAll": "Dismiss All",
    "reminders.sentReminder": "Budget sent {days} days ago with no response",
    "reminders.draftDeadline": "Draft internal deadline expires in {days} days",
    "reminders.draftDeadlineExpired": "Draft internal deadline has expired",
    "reminders.configDays": "Remind after (days)",
    "reminders.draftDeadlineLabel": "Internal deadline (optional)",
    "reminders.noPending": "No pending reminders",
    
    // PDF
    "pdf.export": "Export PDF",
    "pdf.clientView": "Client PDF",
    "pdf.internalView": "Internal PDF",
    "pdf.generating": "Generating PDF...",
    "pdf.technicalNotes": "Technical Notes and Budget Conditions",
    "pdf.premiumRequired": "Premium Feature",
    "pdf.premiumDesc": "Document language, themes, logo, watermark, and technical notes page are available on the Premium plan.",
    "pdf.exportLanguage": "Document Language",
    "pdf.watermark": "Watermark",
    "pdf.watermarkOpacity": "Opacity",
    "pdf.theme": "PDF Theme",
    "pdf.theme.neutral": "Neutral",
    "pdf.theme.professional": "Professional",
    "pdf.theme.modern": "Modern",
    "pdf.boq.chapterSummary": "Chapter Summary",
    "pdf.commercialConditions": "Commercial Conditions",
    "pdf.commercialConditions.validUntil": "Proposal Validity",
    "pdf.commercialConditions.paymentTerms": "Payment Terms",
    "pdf.commercialConditions.executionDeadline": "Execution Deadline",
    "pdf.commercialConditions.expressExclusions": "Express Exclusions",
    "pdf.signature.title": "Agreement and Signature",
    "pdf.signature.date": "Place and Date",
    "pdf.signature.company": "Company / Representative",
    "pdf.signature.stamp": "Signature and Stamp",
    "pdf.popupBlockedFallbackTitle": "Pop-up blocked",
    "pdf.popupBlockedFallbackDescription": "A printable HTML file was downloaded so you can continue the export.",
    "pdf.executiveSummary": "Executive Summary",
    "pdf.proposedValue": "Proposed Value",
    "pdf.subtotal": "Subtotal",
    "pdf.totalWithVat": "Total with VAT",
    "pdf.reverseCharge": "Reverse charge",
    "pdf.internalDocument": "INTERNAL DOCUMENT",
    "pdf.internalDocWarning": "This document is for internal use only and must not be shared with clients.",
    "pdf.status.draft": "DRAFT",
    "pdf.status.sent": "SENT",
    "pdf.status.approved": "APPROVED",
    "pdf.status.rejected": "REJECTED",
    "pdf.status.expired": "EXPIRED",
    "pdf.signature.declarationText": "I hereby acknowledge this proposal and agree to the conditions set out herein.",
    "pdf.signature.client": "Client",
    "pdf.proposalValidUntil": "Proposal valid until",
    "pdf.exclusions": "Exclusions",
    "onboarding.locale.title": "Initial setup",
    "onboarding.locale.description": "Set country, language and currency before continuing to the dashboard.",
    "onboarding.locale.countryPlaceholder": "Select country",
    "onboarding.locale.languagePlaceholder": "Select language",
    "onboarding.locale.currencyPlaceholder": "Select currency",
    "onboarding.locale.continue": "Save and continue",
    "onboarding.locale.savedTitle": "Preferences saved",
    "onboarding.locale.savedDescription": "Country, language and currency were set for this workspace.",
    "onboarding.locale.saveError": "We could not save the initial preferences.",
    "ai.chat.aria": "Orzio assistant",
    "ai.chat.title": "Orzio Assistant",
    "ai.chat.welcome": "Hi. How can I help with your budget?",
    "ai.chat.thinking": "Thinking...",
    "ai.chat.placeholder": "Write a message...",
    "ai.chat.hint": "Enter to send · Shift+Enter new line",
    "ai.chat.error": "Could not contact the assistant.",
    "ai.chat.context.budget": "Budget in context",
    "ai.chat.context.status": "Status",
    "ai.bulkDescribe.title": "Confirm generated descriptions",
    "ai.bulkDescribe.description": "Review the suggestions before updating the items imported from the model.",
    "ai.bulkDescribe.current": "Current description",
    "ai.bulkDescribe.suggested": "AI suggestion",
    "ai.bulkDescribe.empty": "No description defined",
    "ai.bulkDescribe.apply": "Apply descriptions",
    "ai.proposal.title": "Generate proposal email",
    "ai.proposal.description": "Review the subject and body before copying the email.",
    "ai.proposal.subject": "Subject",
    "ai.proposal.body": "Email body",
    "ai.proposal.copy": "Copy email",
    "ai.proposal.copied": "Email copied to clipboard.",
  },
  es: {
    // Common
    "app.name": "Orzio",
    "common.save": "Guardar",
    "common.cancel": "Cancelar",
    "common.next": "Siguiente",
    "common.previous": "Anterior",
    "common.delete": "Eliminar",
    "common.edit": "Editar",
    "common.duplicate": "Duplicar",
    "common.export": "Exportar",
    "common.add": "Añadir",
    "common.remove": "Eliminar",
    "common.loading": "Cargando...",
    "common.close": "Cerrar",
    "common.retry": "Reintentar",
    "common.error": "Error",
    "common.success": "Éxito",
    "common.total": "Total",
    "common.subtotal": "Subtotal",
    "common.unit": "Unidad",
    "common.quantity": "Cantidad",
    "common.item": "Partida",
    "common.price": "Precio",
    "common.description": "Descripción",
    "common.observations": "Observaciones",
    "common.conditions": "Condiciones",
    "common.step": "Paso",
    "common.of": "de",

    // Error boundary
    "errorBoundary.title": "La aplicación encontró un error inesperado.",
    "errorBoundary.description": "Puede intentarlo de nuevo, recargar la página o volver al inicio. Si el problema persiste, contacte con soporte usando la referencia inferior.",
    "errorBoundary.reference": "Referencia:",
    "errorBoundary.retry": "Intentar de nuevo",
    "errorBoundary.reload": "Recargar página",
    "errorBoundary.goHome": "Volver al inicio",
    "errorBoundary.contactSupport": "Contactar soporte",
    "errorBoundary.devDetails": "Detalles técnicos (dev)",
    "errorBoundary.mail.subject": "[Orzio] Error inesperado{eventIdSuffix}",
    "errorBoundary.mail.greeting": "Hola equipo Orzio,",
    "errorBoundary.mail.intro": "Se produjo un error inesperado en la aplicación.",
    "errorBoundary.mail.reference": "Referencia interna: {eventId}",
    "errorBoundary.mail.message": "Mensaje: {message}",
    "errorBoundary.mail.url": "URL: {url}",
    "errorBoundary.mail.userAction": "Descripción de lo que estaba haciendo:",
    
    // Auth
    "auth.login": "Iniciar Sesión",
    "auth.signup": "Crear Cuenta",
    "auth.logout": "Cerrar Sesión",
    "auth.email": "Email",
    "auth.password": "Contraseña",
    "auth.emailPlaceholder": "ingeniero@ejemplo.es",
    "auth.productTagline": "Presupuestación técnica para construcción",
    "auth.signupTitle": "Crear Cuenta",
    "auth.loginTitle": "Iniciar Sesión",
    "auth.signupDesc": "Crea tu cuenta para empezar a estructurar presupuestos",
    "auth.loginDesc": "Inicia sesión en tu cuenta para continuar",
    "auth.loginWithGoogle": "Iniciar sesión con Google",
    "auth.signupWithGoogle": "Crear cuenta con Google",
    "auth.modeSelector": "Elegir modo de autenticación",
    "auth.modeCurrent": "Modo actual",
    "auth.switchToLogin": "Cambiar a iniciar sesión",
    "auth.switchToSignup": "Cambiar a crear cuenta",
    "auth.orEmail": "o email",
    "auth.forgotPassword": "¿Olvidaste la contraseña?",
    "auth.resetTitle": "Recuperar contraseña",
    "auth.resetDesc": "Indica el email de la cuenta y enviaremos un enlace de recuperación.",
    "auth.sendReset": "Enviar email de recuperación",
    "auth.resetEmailSent": "Email enviado",
    "auth.resetEmailDesc": "Revisa tu bandeja de entrada para definir una nueva contraseña.",
    "auth.resetError": "No fue posible procesar la recuperación de contraseña.",
    "auth.newPasswordTitle": "Definir nueva contraseña",
    "auth.newPasswordDesc": "Elige una nueva contraseña para continuar usando Orzio.",
    "auth.newPassword": "Nueva contraseña",
    "auth.confirmPassword": "Confirmar nueva contraseña",
    "auth.passwordMismatch": "Las contraseñas no coinciden.",
    "auth.saveNewPassword": "Guardar nueva contraseña",
    "auth.passwordUpdated": "Contraseña actualizada",
    "auth.passwordUpdatedDesc": "Puedes seguir usando Orzio con la nueva contraseña.",
    "auth.recoveryChecking": "Validando el enlace de recuperación...",
    "auth.recoveryLinkInvalid": "Este enlace de recuperación no es válido o ha expirado. Solicita un nuevo email para restablecer la contraseña.",
    "auth.backToLogin": "Volver a iniciar sesión",
    "auth.googleError": "No fue posible iniciar sesión con Google.",
    "auth.hasAccount": "¿Ya tienes cuenta?",
    "auth.noAccount": "¿No tienes cuenta?",
    "auth.accountCreated": "Cuenta creada",
    "auth.checkEmail": "Verifica tu email para confirmar la cuenta.",
    "auth.invalidCredentials": "Email o contraseña incorrectos.",
    "auth.emailExists": "Este email ya está registrado.",
    "auth.invalidEmail": "Email inválido",
    "auth.passwordTooShort": "La contraseña debe tener al menos 6 caracteres",
    
    // Landing
    "landing.hero.title": "Presupuestos Técnicos Estructurados",
    "landing.hero.subtitle": "Herramienta profesional para ingenieros y oficinas técnicas. Estructura, justifica y estandariza tus presupuestos de ingeniería.",
    "landing.cta.start": "Comenzar Ahora",
    "landing.cta.demo": "Ver Demostración",
    "landing.cta.create": "Crear Cuenta Gratuita",
    "landing.principles.title": "Principios Fundamentales",
    "landing.principles.deterministic.title": "Cálculos Determinísticos",
    "landing.principles.deterministic.desc": "Todos los valores se calculan con lógica precisa. Sin estimaciones de IA para números críticos.",
    "landing.principles.structured.title": "Flujo Estructurado",
    "landing.principles.structured.desc": "Proceso guiado en pasos obligatorios. Garantiza que ningún elemento esencial quede fuera.",
    "landing.principles.separation.title": "Separación Clara",
    "landing.principles.separation.desc": "Distinción entre datos técnicos internos e información visible para el cliente.",
    "landing.features.title": "Funcionalidades",
    "landing.features.guided": "Creación guiada de presupuestos técnicos",
    "landing.features.costs": "Gestión de bloques de costes y mano de obra",
    "landing.features.risk": "Cálculo automático de riesgo y margen",
    "landing.features.assumptions": "Supuestos técnicos estructurados",
    "landing.features.pdf": "Exportación PDF profesional",
    "landing.features.versions": "Historial de versiones",
    "landing.features.ai": "Sugerencias de alcance asistidas por IA",
    "landing.features.gdpr": "PDF con vistas interna y cliente separadas",
    "landing.features.projects": "Presupuestos de Desarrollo de Proyectos",
    "landing.features.works": "Presupuestos de Ejecución de Obras",
    "landing.cta.title": "Profesionaliza tus presupuestos",
    "landing.cta.desc": "Únete a ingenieros y oficinas técnicas que ya estructuran sus presupuestos de forma profesional.",
    "landing.footer": "Contexto Europeo. Todos los derechos reservados.",
    
    // Dashboard
    "dashboard.title": "Presupuestos",
    "dashboard.subtitle": "Gestionar y crear presupuestos técnicos",
    "dashboard.newBudget": "Nuevo Presupuesto",
    "dashboard.stats.total": "Total de Presupuestos",
    "dashboard.stats.drafts": "Borradores",
    "dashboard.stats.sent": "Enviados",
    "dashboard.stats.approved": "Aprobados",
    "dashboard.stats.rejected": "Rechazados",
    "dashboard.stats.expired": "Expirados",
    "dashboard.filters": "Filtros",
    "dashboard.allBudgets": "Todos",
    "dashboard.list.title": "Lista de Presupuestos",
    "dashboard.list.version": "Versión",
    "dashboard.list.totalPrice": "Precio Total",
    "dashboard.status.draft": "Borrador",
    "dashboard.status.sent": "Enviado",
    "dashboard.status.approved": "Aprobado",
    "dashboard.status.rejected": "Rechazado",
    "dashboard.status.expired": "Expirado",
    "dashboard.approvalValue": "¿Valor final acordado?",
    "dashboard.discountApplied": "Descuento aplicado",
    "dashboard.reopen": "Reabrir",
    "dashboard.validity": "Validez",
    "dashboard.validityDays": "días de validez",
    "dashboard.exportPdf": "Exportar PDF",
    "dashboard.markSent": "Marcar como Enviado",
    "dashboard.markApproved": "Marcar como Aprobado",
    "dashboard.markRejected": "Marcar como Rechazado",
    "dashboard.reopenBudget": "Reabrir Presupuesto",
    "dashboard.approveTitle": "Aprobar Presupuesto",
    "dashboard.approveDesc": "Introduzca el valor final acordado con el cliente.",
    "dashboard.agreedValue": "Valor acordado",
    "dashboard.unplannedDiscount": "Descuento no planificado",
    "dashboard.confirm": "Confirmar",
    "dashboard.statusChanged": "Estado cambiado con éxito",
    "dashboard.expired.auto": "Expirado automáticamente",
    
    // Budget Types
    "budgetType.title": "Tipo de Presupuesto",
    "budgetType.subtitle": "Elige el tipo de presupuesto que deseas crear",
    "budgetType.projects.title": "Desarrollo de Proyectos",
    "budgetType.projects.desc": "Presupuestos para servicios de ingeniería y proyectos técnicos. Basado en horas y especialidades.",
    "budgetType.works.title": "Ejecución de Obras",
    "budgetType.works.desc": "Presupuestos para construcción y contratación. Basado en partidas, cantidades y precios unitarios.",
    "budgetType.unavailable": "No incluido en el plan",
    "budgetType.warning": "Esta elección no puede cambiarse después de crear el presupuesto.",
    
    // Wizard Steps
    "wizard.step.type": "Tipo",
    "wizard.step.identification": "Identificación",
    "wizard.step.scope": "Alcance",
    "wizard.step.costs": "Costes",
    "wizard.step.risk": "Riesgo",
    "wizard.step.assumptions": "Supuestos",
    "wizard.step.executionTime": "Plazo",
    "wizard.step.review": "Revisión",
    "wizard.newBudget": "Nuevo Presupuesto",
    "wizard.saveBudget": "Guardar Presupuesto",
    "wizard.saving": "Guardando...",
    "wizard.saved": "Presupuesto guardado",
    "wizard.savedDesc": "Tu presupuesto se ha guardado correctamente.",
    
    // Identification Step
    "identification.title": "Identificación del Proyecto",
    "identification.subtitle": "Información básica sobre el proyecto a presupuestar",
    "identification.projectName": "Nombre del Proyecto",
    "identification.projectNamePlaceholder": "Ej: Edificio Residencial Tajo",
    "identification.projectType": "Tipo de Proyecto",
    "identification.projectType.newBuild": "Construcción Nueva",
    "identification.projectType.rehabilitation": "Rehabilitación",
    "identification.projectType.industrial": "Industrial",
    "identification.projectType.technicalService": "Servicio Técnico",
    "identification.projectType.other": "Otro",
    "identification.projectType.otherPlaceholder": "Especifique el tipo de proyecto...",
    "identification.selectType": "Seleccionar tipo",
    "identification.selectLevel": "Seleccionar nivel",
    "identification.location": "Ubicación",
    "identification.locationPlaceholder": "Ej: Madrid, España",
    "identification.clientType": "Tipo de cliente",
    "identification.clientType.private": "Particular",
    "identification.clientType.company": "Empresa",
    "identification.clientType.public": "Entidad Pública",
    "identification.definitionLevel": "Nivel de Definición del Proyecto",
    "identification.definitionLevel.low": "Bajo (Idea/Concepto)",
    "identification.definitionLevel.medium": "Medio (Estudio Previo)",
    "identification.definitionLevel.high": "Alto (Proyecto Básico/Ejecución)",
    "identification.definitionLevelHint": "Cuanto menor sea la definición, mayor será el riesgo asociado",
    
    // Scope Step (Projects)
    "scope.projects.title": "Alcance Técnico",
    "scope.projects.subtitle": "Selecciona los servicios de ingeniería incluidos en este presupuesto",
    "scope.projects.suggest": "Sugerir Alcance",
    "scope.projects.additionalScope": "Alcance Adicional",
    "scope.projects.additionalScopePlaceholder": "Describe servicios adicionales no listados arriba...",
    "scope.projects.selected": "servicio(s) seleccionado(s)",
    "scope.projects.selectAtLeastOne": "Selecciona al menos un servicio para continuar",
    
    // Scope Step (Works)
    "scope.works.title": "Alcance de la Obra",
    "scope.works.subtitle": "Define la tipología y condiciones generales de la obra",
    "scope.works.typology": "Tipología de Obra",
    "scope.works.typology.construcao_nova": "Construcción Nueva",
    "scope.works.typology.reabilitacao": "Rehabilitación / Remodelación",
    "scope.works.typology.industrial_l1": "Industrial",
    "scope.works.typology.obra_parcial": "Obra Parcial / Especializada",
    "scope.works.typology.infraestrutura": "Infraestructura",
    "scope.works.typology.instalacoes_tecnicas": "Instalaciones Técnicas",
    "scope.works.typology.manutencao": "Mantenimiento / Conservación",
    "scope.works.typology.outra": "Otra",
    "scope.works.typology.outraPlaceholder": "Especifique la tipología...",
    "scope.works.subTypologyLabel": "Subtipología (opcional)",
    "scope.works.sub.habitacao": "Vivienda",
    "scope.works.sub.comercial": "Comercial",
    "scope.works.sub.misto": "Mixto",
    "scope.works.sub.edificio_publico": "Edificio Público",
    "scope.works.sub.estrutural": "Estructural",
    "scope.works.sub.interior": "Interior",
    "scope.works.sub.fachadas": "Fachadas",
    "scope.works.sub.integral": "Integral",
    "scope.works.sub.nave": "Nave Industrial",
    "scope.works.sub.plataformas": "Plataformas",
    "scope.works.sub.instalacoes": "Instalaciones",
    "scope.works.sub.estrutura_metalica": "Estructura Metálica",
    "scope.works.sub.coberturas": "Cubiertas",
    "scope.works.sub.fachadas_parcial": "Fachadas",
    "scope.works.sub.impermeabilizacao": "Impermeabilización",
    "scope.works.sub.arruamentos": "Vialidad",
    "scope.works.sub.saneamento": "Saneamiento",
    "scope.works.sub.abastecimento": "Abastecimiento",
    "scope.works.sub.avac": "Climatización",
    "scope.works.sub.eletricidade": "Electricidad",
    "scope.works.sub.telecomunicacoes": "Telecomunicaciones",
    "scope.works.sub.gas": "Gas",
    "scope.works.sub.preventiva": "Preventivo",
    "scope.works.sub.corretiva": "Correctivo",
    "scope.works.sub.conservacao": "Conservación",
    "scope.works.sub.other": "Otra",
    "scope.works.sub.otherPlaceholder": "Especifique...",
    "scope.works.selectionsCount": "selección(es)",
    "scope.works.generalObservations": "Observaciones Generales",
    "scope.works.generalObservationsPlaceholder": "Describe las condiciones generales de la obra...",
    "scope.works.exclusions": "Condiciones y Exclusiones",
    "scope.works.exclusionsPlaceholder": "Lista las condiciones y exclusiones...",
    
    // Cost Blocks Step (Projects)
    "costs.projects.title": "Bloques de Costes",
    "costs.projects.subtitle": "Define los costes de mano de obra y otros costes asociados",
    "costs.projects.labor": "Mano de Obra",
    "costs.projects.indirect": "Costes Indirectos",
    "costs.projects.addLine": "Añadir Línea",
    "costs.projects.addBlock": "Añadir Bloque de Costes",
    "costs.projects.role": "Función",
    "costs.projects.hours": "Horas",
    "costs.projects.hourlyRate": "€/Hora",
    "costs.projects.baseCost": "Coste Total Base",
    "costs.projects.beforeRiskMargin": "Antes de riesgo y margen",
    "costs.projects.roles.seniorEngineer": "Ingeniero Senior",
    "costs.projects.roles.engineer": "Ingeniero",
    "costs.projects.roles.juniorEngineer": "Ingeniero Junior",
    "costs.projects.roles.technician": "Técnico",
    "costs.projects.roles.draftsman": "Delineante",
    "costs.projects.roles.admin": "Administrativo",
    
    // Cost Items Step (Works)
    "costs.works.title": "Mediciones",
    "costs.works.subtitle": "Define las partidas con cantidades y precios unitarios",
    "costs.works.import.title": "Importar mediciones Orzio Takeoff",
    "costs.works.import.subtitle": "Acepta ficheros .xlsx emitidos por el Add-in Orzio Takeoff para Revit con esquema técnico válido.",
    "costs.works.import.button": "Importar XLSX",
    "costs.works.import.itemsImported": "partidas importadas de",
    "costs.works.import.genericError": "No fue posible importar el fichero Orzio Takeoff.",
    "costs.works.addItem": "Añadir Partida",
    "costs.works.itemDescription": "Descripción de la Partida",
    "costs.works.itemDescriptionPlaceholder": "Ej: Suministro y montaje de estructura metálica...",
    "costs.works.unitPrice": "Precio Unitario",
    "costs.works.totalPrice": "Precio Total",
    "costs.works.composition": "Composición",
    "costs.works.openComposition": "Abrir Composición",
    "costs.works.closeComposition": "Cerrar Composición",
    "costs.works.addSubItem": "Añadir Subpartida",
    "costs.works.subItem.labor": "Mano de Obra",
    "costs.works.subItem.materials": "Materiales",
    "costs.works.subItem.equipment": "Equipo",
    "costs.works.subItem.other": "Otros",
    "costs.works.referencePrice": "Precio de Referencia",
    "costs.works.referencePriceHint": "Opcional - usado solo como orientación",
    "costs.works.units.m2": "m²",
    "costs.works.units.ml": "ml",
    "costs.works.units.kg": "kg",
    "costs.works.units.un": "ud",
    "costs.works.units.h": "h",
    "costs.works.units.vg": "pa",
    "costs.works.units.m": "m",
    "costs.works.units.m3": "m³",
    "costs.works.units.t": "t",
    "costs.works.units.day": "día",
    "costs.works.units.week": "semana",
    "costs.works.units.lot": "lote",
    "costs.works.units.percent": "%",
    "costs.works.units.global": "global",
    "costs.works.totalBase": "Total Base",
    
    // Risk & Margin Step
    "risk.title": "Riesgo y Margen",
    "risk.subtitle": "Define el nivel de riesgo y el margen de beneficio deseado",
    "risk.level": "Nivel de Riesgo del Proyecto",
    "risk.level.low": "Bajo",
    "risk.level.low.desc": "Proyecto bien definido, cliente experimentado, condiciones conocidas",
    "risk.level.medium": "Medio",
    "risk.level.medium.desc": "Definición parcial, algunas incertidumbres técnicas o de alcance",
    "risk.level.high": "Alto",
    "risk.level.high.desc": "Proyecto con muchas incógnitas, cliente inexperto, plazos ajustados",
    "risk.customPercentage": "Porcentaje de Riesgo Personalizado",
    "risk.margin": "Margen de Beneficio",
    "risk.marginBelowMin": "Margen por debajo del mínimo de la organización",
    "risk.indirectCosts": "Costes Indirectos",
    "risk.indirectCostsHint": "Gastos generales, administración, etc.",
    "risk.calculation": "Cálculo en Tiempo Real",
    "risk.baseCost": "Coste Base",
    "risk.riskProvision": "Provisión de Riesgo",
    "risk.costWithRisk": "Coste con Riesgo",
    "risk.marginValue": "Margen",
    "risk.finalPrice": "Precio Final",
    "risk.marginBelowMinWithValue": "Margen por debajo del mínimo de la organización ({min}%).",
    "risk.ai.title": "Sugerencia de margen con IA",
    "risk.ai.loading": "Analizando histórico y contexto comercial...",
    "risk.ai.sampleConfidence": "Muestra: {sampleSize} presupuesto(s) · Confianza {confidence}",
    "risk.ai.apply": "Aplicar {margin}%",
    "risk.ai.error": "No fue posible sugerir el margen.",
    "risk.ai.confidence.high": "Alta",
    "risk.ai.confidence.medium": "Media",
    "risk.ai.confidence.low": "Baja",

    // Budget editor
    "budgetEditor.unknownError": "Error desconocido.",
    "budgetEditor.createError": "No fue posible crear el presupuesto",
    "budgetEditor.createErrorWithMessage": "No fue posible crear el presupuesto: {message}",
    "budgetEditor.snapshotLoadError": "No fue posible cargar la instantánea. Mostrando datos demo.",
    "budgetEditor.demoMode": "Editor en modo demo",
    "budgetEditor.loadBudgetFailure": "Fallo al cargar el presupuesto.",
    "budgetEditor.autoSaveError": "No fue posible guardar automáticamente",
    "budgetEditor.manualSaveSuccess": "Presupuesto guardado",
    "budgetEditor.manualSaveSuccessDescription": "Los cambios se guardaron correctamente.",
    "budgetEditor.manualSaveError": "No fue posible guardar",
    "budgetEditor.approvedLocally": "Presupuesto aprobado localmente",
    "budgetEditor.saveToOpenWork": "Cree o guarde el presupuesto para abrir la obra.",
    "budgetEditor.approved": "Presupuesto aprobado",
    "budgetEditor.approveError": "No fue posible aprobar",
    "budgetEditor.saveBeforeCreateWork": "Guarde el presupuesto antes de crear la obra",
    "budgetEditor.executionNeedsPersistedBudget": "La ejecución necesita un presupuesto persistido.",
    "budgetEditor.workCreated": "Obra creada",
    "budgetEditor.workAvailableInMenu": "El presupuesto aprobado ya aparece en el menú Obras.",
    "budgetEditor.createWorkError": "No fue posible crear la obra",
    "budgetEditor.statusUpdated": "Estado actualizado",
    "budgetEditor.statusUpdateError": "No fue posible actualizar el estado",
    "budgetEditor.loading": "Cargando editor de presupuesto...",
    "budgetEditor.autoSaveBanner": "Fallo en el guardado automático. Los cambios siguen en el editor, pero aún no se han guardado. {saveError}",
    "budgetEditor.previewPdf": "Vista previa de PDF",
    "budgetEditor.generateEmail": "Generar email",
    "budgetEditor.createWork": "Crear Obra",
    "budgetEditor.actionNotWiredDescription": "Acción lista en la UI; la integración queda para el coordinador o Worker E.",
    
    // Assumptions Step
    "assumptions.title": "Supuestos Técnicos",
    "assumptions.subtitle": "Define las condiciones y supuestos en los que se basa el presupuesto",
    "assumptions.generate": "Generar Supuestos",
    "assumptions.add": "Añadir Supuesto",
    "assumptions.placeholder": "Describe el supuesto...",
    "assumptions.visibleToClient": "Visible para el cliente",
    "assumptions.internalOnly": "Solo interno",
    "assumptions.count": "supuestos",
    "assumptions.visible": "visibles",
    "assumptions.internal": "internos",
    
    // Review Step
    "review.title": "Revisión del Presupuesto",
    "review.subtitle": "Revisa todos los detalles antes de guardar",
    "review.verification": "Verificación",
    "review.internalView": "Vista Interna",
    "review.clientView": "Vista Cliente",
    "review.identification": "Identificación",
    "review.project": "Proyecto",
    "review.type": "Tipo",
    "review.location": "Ubicación",
    "review.client": "Cliente",
    "review.definition": "Definición",
    "review.scope": "Alcance",
    "review.services": "servicios",
    "review.items": "partidas",
    "review.financialSummary": "Resumen Financiero",
    "review.servicesIncluded": "Servicios Incluidos",
    "review.itemsIncluded": "Partidas Incluidas",
    "review.totalValue": "Valor Total",
    "review.vatNotIncluded": "IVA no incluido",
    "tax.name.pt": "IVA",
    "tax.name.es": "IVA",
    "tax.name.fr": "IVA",
    "tax.name.uk": "IVA",
    "tax.name.default": "Impuesto",
    "tax.showWithTax": "¿Mostrar valores con impuesto?",
    "tax.withoutTax": "Total sin impuesto",
    "tax.percentage": "Porcentaje de impuesto",
    "tax.value": "Valor del impuesto",
    "tax.totalWithTax": "Total con impuesto",
    "tax.reverseCharge": "Inversión del sujeto pasivo",
    "tax.reverseChargeHint": "Aplicable al sector de la construcción",
    "tax.unusualWarning": "Valor inusual",
    "tax.reverseChargeNote": "Se aplica el mecanismo de inversión del sujeto pasivo según la legislación vigente. El IVA será liquidado por el adquirente.",
    "variations.createAdditional": "Crear Trabajo Adicional",
    "variations.onlyAfterSave": "Los trabajos adicionales solo pueden crearse después de guardar el presupuesto principal.",
    "variations.referenceBudget": "Referencia al presupuesto",
    
    // Variation Orders
    "variations.title": "Trabajos Adicionales",
    "variations.subtitle": "Trabajos adicionales al presupuesto principal",
    "variations.add": "Añadir Trabajo Adicional",
    "variations.number": "Nº",
    "variations.description": "Descripción",
    "variations.value": "Valor",
    "variations.total": "Total Trabajos Adicionales",
    "variations.exportSeparate": "Exportar Separadamente",
    "variations.exportAsAnnex": "Exportar como Anexo",
    
    // Execution Time
    "executionTime.title": "Plazo de Ejecución Estimado",
    "executionTime.aiSuggest": "Sugerir Plazo",
    "executionTime.days": "días laborables",
    "executionTime.range": "Intervalo estimado",
    "executionTime.disclaimer": "Duración indicativa, sujeta a condiciones del sitio",
    "executionTime.visibleToClient": "Mostrar al cliente",
    "executionTime.internalOnly": "Solo interno",
    
    // Services
    "services.architecture": "Proyecto de Arquitectura",
    "services.structural": "Proyecto de Estructuras",
    "services.hydraulic": "Proyecto de Fontanería",
    "services.electrical": "Proyecto de Instalaciones Eléctricas",
    "services.hvac": "Proyecto de Climatización",
    "services.gas": "Proyecto de Gas",
    "services.fire": "Proyecto de Seguridad Contra Incendios",
    "services.acoustic": "Proyecto de Acondicionamiento Acústico",
    "services.thermal": "Proyecto de Comportamiento Térmico",
    "services.telecom": "Proyecto de Telecomunicaciones",
    "services.coordination": "Coordinación de Proyecto",
    "services.supervision": "Dirección de Obra",
    
    // Settings
    "settings.title": "Configuración de la Empresa",
    "settings.subtitle": "Configure la información de su empresa para los documentos",
    "settings.companyInfo": "Información de la Empresa",
    "settings.companyInfoDesc": "Estos datos aparecen en los PDF y documentos generados",
    "settings.companyName": "Nombre de la Empresa",
    "settings.companyNamePlaceholder": "Ej: Ingeniería Silva, S.L.",
    "settings.logo": "Logotipo",
    "settings.logoHint": "PNG o JPG, máx. 5MB. Aparece en la esquina superior izquierda del PDF.",
    "settings.address": "Dirección Completa",
    "settings.addressPlaceholder": "Ej: Calle de la Ingeniería, nº 10",
    "settings.postalCode": "Código Postal",
    "settings.country": "País",
    "settings.phone": "Teléfono",
    "settings.email": "Email",
    "settings.website": "Sitio Web",
    "settings.taxId": "NIF / CIF",
    "settings.licenseNumber": "Nº de Licencia",
    "settings.licenseNumberPlaceholder": "Opcional",
    "settings.technicalResponsible": "Responsable Técnico",
    "settings.technicalResponsiblePlaceholder": "Nombre del responsable técnico",
    "settings.signature": "Firma",
    "settings.signatureDesc": "Imagen de firma para incluir en los documentos (opcional)",
    "settings.budgetNumbering": "Numeración de Presupuestos",
    "settings.budgetNumberingDesc": "Configure el prefijo para la numeración automática",
    "settings.prefix": "Prefijo",
    "settings.previewLabel": "Ejemplo",
    "settings.pdfPreview": "Vista Previa del Encabezado PDF",
    "settings.pdfPreviewDesc": "Así aparecerá el encabezado en sus documentos",
    "settings.pdfPreviewHint": "Vista previa del encabezado del documento",
    "settings.saved": "Configuración guardada correctamente",
    "settings.invalidImageFormat": "Formato inválido. Use PNG o JPG.",
    "settings.imageTooLarge": "Imagen demasiado grande. Máximo 5MB.",
    "dashboard.settings": "Configuración",

    // IFC Import
    "ifcImport.upload.title": "Importar modelo IFC",
    "ifcImport.upload.subtitle": "Arrastra un archivo .ifc o selecciónalo en tu equipo. Después de la lectura, podrás crear un presupuesto nuevo o asociar el modelo a uno existente.",
    "ifcImport.upload.button": "Seleccionar IFC",
    "ifcImport.upload.parsing": "Analizando IFC...",
    "ifcImport.summary.title": "IFC importado correctamente",
    "ifcImport.summary.total": "elementos importados",
    "ifcImport.summary.assign": "Asignar composiciones",
    "ifcImport.summary.warnings": "Avisos",
    "ifcImport.table.empty": "Todavía no hay elementos IFC importados.",
    "ifcImport.table.type": "Tipo IFC",
    "ifcImport.table.name": "Nombre",
    "ifcImport.table.level": "Nivel",
    "ifcImport.table.quantity": "Cantidad",
    "ifcImport.table.unit": "Unidad",
    "ifcImport.table.composition": "Composición",
    "ifcImport.table.unitCost": "Coste unitario",
    "ifcImport.table.assign": "Asignar",
    "ifcImport.mapper.title": "Mapeo de composiciones",
    "ifcImport.mapper.generate": "Generar partidas",
    "ifcImport.success.persisted": "Elementos IFC guardados",
    "ifcImport.error.invalidFile": "Selecciona un archivo .ifc válido.",
    "ifcImport.error.fileTooLarge": "El archivo IFC supera el limite de 100MB.",
    "ifcImport.error.tooLarge": "El archivo IFC supera el limite de 100MB.",
    "ifcImport.error.tooSmall": "El archivo IFC es demasiado pequeno para ser valido.",
    "ifcImport.error.notIfc": "Selecciona un archivo IFC .ifc valido. Las variantes comprimidas no se aceptan.",
    "ifcImport.error.budgetForbidden": "El presupuesto seleccionado no pertenece a la organizacion activa.",
    "ifcImport.error.invalidMetadata": "Los metadatos del archivo IFC son invalidos.",
    "ifcImport.error.uploadInitFailed": "No fue posible iniciar la importacion IFC.",
    "ifcImport.error.invalidIfcHeader": "El archivo no contiene un encabezado STEP IFC valido (ISO-10303-21;).",
    "ifcImport.error.generic": "No fue posible analizar el archivo IFC.",
    "ifcImport.error.readFile": "No fue posible leer el archivo.",
    "ifcImport.error.persist": "No fue posible guardar los elementos IFC.",
    "ifcImport.error.assign": "No fue posible asignar la composición.",
    "ifcImport.error.authRequired": "Inicia sesión para importar IFC.",
    "ifcImport.error.noOrganization": "Organización no encontrada.",
    "ifcImport.error.loadCompositions": "No fue posible cargar composiciones.",
    "ifcImport.warning.emptyFile": "El archivo IFC está vacío.",
    "ifcImport.warning.noStepRecords": "No se encontraron registros STEP en el archivo.",
    "ifcImport.warning.noElements": "No se encontraron elementos constructivos soportados.",
    "ifcImport.warning.largeModel": "Modelo con más de 5000 elementos; la revisión puede ser más lenta.",
    "compositionPicker.placeholder": "Seleccionar composición",
    "compositionPicker.search": "Buscar por nombre o código...",
    "compositionPicker.empty": "No se encontraron composiciones.",
    "compositionPicker.clear": "Limpiar composición",

    // BOQ
    "boq.export.menu": "Exportar BOQ / XLSX",
    "boq.export.button": "Exportar BOQ",
    "boq.export.download": "Download XLSX",
    "boq.dialog.title": "Mediciones",
    "boq.table.description": "Descripción",
    "boq.table.unit": "Ud.",
    "boq.table.quantity": "Cant.",
    "boq.table.unitCost": "P.U. €",
    "boq.table.total": "Total €",
    "boq.summary.risk": "Riesgo",
    "boq.summary.costWithRisk": "Coste + Riesgo",
    "boq.summary.margin": "Margen",
    "boq.section.manual": "Manual",
    "boq.pdf.title": "Mediciones",
    "boq.pdf.toggle": "Incluir Mediciones",
    "budgetReview.rule.noZeroUnitPrice": "Todos los ítems directos deben tener precio > 0",
    "budgetReview.rule.noZeroQuantity": "Todos los ítems deben tener cantidad > 0",
    "budgetReview.rule.unmappedIfc": "Elementos IFC sin composición asignada",
    "budgetReview.rule.noWorkItems": "El presupuesto debe tener al menos un ítem de obra",
    "budgetReview.rule.noRiskMargin": "El margen del presupuesto es 0%",
    "budgetReview.violation.missingUnitPrice": "Precio unitario faltante",
    "budgetReview.violation.zeroQuantity": "Cantidad cero o negativa",
    "budgetReview.violation.noComposition": "Sin composición asignada",
    "budgetReview.violation.noWorkItems": "Ningún ítem de obra definido",
    "budgetReview.violation.noMargin": "Considere agregar un margen de contingencia",
    "budgetReview.badge.complete": "Completo",
    "budgetReview.badge.errors": "{{count}} error(es)",
    "budgetReview.badge.warnings": "{{count}} aviso(s)",
    "budgetReview.panel.title": "Análisis del Presupuesto",
    "budgetReview.panel.passed": "reglas verificadas",
    "budgetReview.panel.override": "Exportar de todas formas",
    "ifcImport.wizard.button": "Importar IFC",
    "ifcImport.wizard.sheetTitle": "Importar desde archivo IFC",
    "ifcImport.wizard.success": "{{count}} partidas generadas",
    "ifcImport.wizard.saveFirst": "Guarda el presupuesto antes de importar IFC.",
    
    // Reminders & Deadlines
    "reminders.title": "Recordatorios",
    "reminders.popup.title": "Recordatorios Pendientes",
    "reminders.popup.dismiss": "Descartar",
    "reminders.popup.dismissAll": "Descartar Todos",
    "reminders.sentReminder": "Presupuesto enviado hace {days} días sin respuesta",
    "reminders.draftDeadline": "Plazo interno del borrador expira en {days} días",
    "reminders.draftDeadlineExpired": "El plazo interno del borrador ha expirado",
    "reminders.configDays": "Recordar después de (días)",
    "reminders.draftDeadlineLabel": "Plazo interno (opcional)",
    "reminders.noPending": "Sin recordatorios pendientes",
    
    // PDF
    "pdf.export": "Exportar PDF",
    "pdf.clientView": "PDF Cliente",
    "pdf.internalView": "PDF Interno",
    "pdf.generating": "Generando PDF...",
    "pdf.technicalNotes": "Notas Técnicas y Condiciones del Presupuesto",
    "pdf.premiumRequired": "Función Premium",
    "pdf.premiumDesc": "Idioma del documento, temas, logotipo, marca de agua y página de notas técnicas están disponibles en el plan Premium.",
    "pdf.exportLanguage": "Idioma del Documento",
    "pdf.watermark": "Marca de agua",
    "pdf.watermarkOpacity": "Opacidad",
    "pdf.theme": "Tema del PDF",
    "pdf.theme.neutral": "Neutro",
    "pdf.theme.professional": "Profesional",
    "pdf.theme.modern": "Moderno",
    "pdf.boq.chapterSummary": "Resumen por Capítulo",
    "pdf.commercialConditions": "Condiciones Comerciales",
    "pdf.commercialConditions.validUntil": "Validez de la Propuesta",
    "pdf.commercialConditions.paymentTerms": "Condiciones de Pago",
    "pdf.commercialConditions.executionDeadline": "Plazo de Ejecución",
    "pdf.commercialConditions.expressExclusions": "Exclusiones Expresas",
    "pdf.signature.title": "Conformidad y Firma",
    "pdf.signature.date": "Lugar y Fecha",
    "pdf.signature.company": "Empresa / Responsable",
    "pdf.signature.stamp": "Firma y Sello",
    "pdf.popupBlockedFallbackTitle": "Pop-up bloqueado",
    "pdf.popupBlockedFallbackDescription": "Se descargó un archivo HTML imprimible para continuar la exportación.",
    "pdf.executiveSummary": "Resumen Ejecutivo",
    "pdf.proposedValue": "Valor Propuesto",
    "pdf.subtotal": "Subtotal",
    "pdf.totalWithVat": "Total con IVA",
    "pdf.reverseCharge": "Inversión del sujeto pasivo",
    "pdf.internalDocument": "DOCUMENTO INTERNO",
    "pdf.internalDocWarning": "Este documento es de uso exclusivamente interno y no debe compartirse con clientes.",
    "pdf.status.draft": "BORRADOR",
    "pdf.status.sent": "ENVIADO",
    "pdf.status.approved": "APROBADO",
    "pdf.status.rejected": "RECHAZADO",
    "pdf.status.expired": "EXPIRADO",
    "pdf.signature.declarationText": "Declaro que he tomado conocimiento del presente presupuesto y estoy de acuerdo con las condiciones establecidas.",
    "pdf.signature.client": "Cliente",
    "pdf.proposalValidUntil": "Propuesta válida hasta",
    "pdf.exclusions": "Exclusiones",
    "onboarding.locale.title": "Configuración inicial",
    "onboarding.locale.description": "Define país, idioma y moneda antes de continuar al panel.",
    "onboarding.locale.countryPlaceholder": "Seleccionar país",
    "onboarding.locale.languagePlaceholder": "Seleccionar idioma",
    "onboarding.locale.currencyPlaceholder": "Seleccionar moneda",
    "onboarding.locale.continue": "Guardar y continuar",
    "onboarding.locale.savedTitle": "Preferencias guardadas",
    "onboarding.locale.savedDescription": "País, idioma y moneda definidos para el espacio de trabajo.",
    "onboarding.locale.saveError": "No se han podido guardar las preferencias iniciales.",
    "ai.chat.aria": "Asistente Orzio",
    "ai.chat.title": "Asistente Orzio",
    "ai.chat.welcome": "Hola. ¿Cómo puedo ayudar con tu presupuesto?",
    "ai.chat.thinking": "Pensando...",
    "ai.chat.placeholder": "Escribe un mensaje...",
    "ai.chat.hint": "Enter para enviar · Shift+Enter nueva línea",
    "ai.chat.error": "No se ha podido contactar con el asistente.",
    "ai.chat.context.budget": "Presupuesto en contexto",
    "ai.chat.context.status": "Estado",
    "ai.bulkDescribe.title": "Confirmar descripciones generadas",
    "ai.bulkDescribe.description": "Revisa las sugerencias antes de actualizar las partidas importadas del modelo.",
    "ai.bulkDescribe.current": "Descripción actual",
    "ai.bulkDescribe.suggested": "Sugerencia de la IA",
    "ai.bulkDescribe.empty": "Sin descripción definida",
    "ai.bulkDescribe.apply": "Aplicar descripciones",
    "ai.proposal.title": "Generar correo de propuesta",
    "ai.proposal.description": "Revisa el asunto y el cuerpo antes de copiar el correo.",
    "ai.proposal.subject": "Asunto",
    "ai.proposal.body": "Cuerpo del correo",
    "ai.proposal.copy": "Copiar correo",
    "ai.proposal.copied": "Correo copiado al portapapeles.",
    "dashboard.preSendReview.title": "Revisión antes de enviar",
    "dashboard.preSendReview.description": "Orzio ha encontrado puntos que conviene revisar antes de marcar el presupuesto como enviado.",
    "dashboard.preSendReview.continue": "Enviar igualmente",
    "dashboard.preSendReview.back": "Volver al presupuesto",
    "dashboard.preSendReview.summary": "Resumen",
    "assumptions.defaultProjectType": "Presupuestación técnica de construcción",
    "settings.language": "Idioma",
    "settings.currency": "Moneda",
  },
};
